import React from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AccessTimeOutlined,
  AddOutlined,
  AddBusinessOutlined,
  BadgeOutlined,
  CalendarMonthOutlined,
  CloudUploadOutlined,
  DeleteOutlineOutlined,
  DirectionsCarFilledOutlined,
  EditOutlined,
  FactCheckOutlined,
  FileDownloadOutlined,
  LogoutOutlined,
  LocalPhoneOutlined,
  LocalShippingOutlined,
  LocationOnOutlined,
  ManageSearchOutlined,
  NotificationsOutlined,
  PaymentsOutlined,
  PersonOutlined,
  SaveOutlined,
  SettingsOutlined,
  ShieldOutlined,
  SpeedOutlined,
  TaskAltOutlined,
  VerifiedUserOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';

const VEHICLE_CATEGORIES = [
  'Bakkies',
  'Vans',
  'Small trucks',
  'Refrigerated trucks',
  'Tankers',
  'Flatbeds',
  'Abnormal load trucks',
];

const VEHICLE_LISTINGS = [
  {
    type: 'Bakkies',
    capacity: '1 ton utility load',
    location: 'Cape Town, Western Cape',
    availability: 'Weekdays',
    pricing: 'R950 / day',
    owner: 'Cape Fleet Partners',
    image:
      'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?auto=format&fit=crop&w=900&q=80',
  },
  {
    type: 'Vans',
    capacity: '1.5 ton panel van',
    location: 'Gqeberha, Eastern Cape',
    availability: 'Available tomorrow',
    pricing: 'R1,350 / day',
    owner: 'Algoa Urban Logistics',
    image:
      'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=80',
  },
  {
    type: 'Small trucks',
    capacity: '4 ton box body',
    location: 'Bloemfontein, Free State',
    availability: 'Available this week',
    pricing: 'R2,400 / day',
    owner: 'Central Route Carriers',
    image:
      'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&w=900&q=80',
  },
  {
    type: 'Refrigerated trucks',
    capacity: '8 ton chilled cargo',
    location: 'Johannesburg, Gauteng',
    availability: 'Available today',
    pricing: 'R4,950 / day',
    owner: 'Northline Cold Chain',
    image:
      'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=80',
  },
  {
    type: 'Tankers',
    capacity: '32,000 litre fuel tanker',
    location: 'Secunda, Mpumalanga',
    availability: 'Monthly contracts',
    pricing: 'R38 / km',
    owner: 'Highveld Bulk Transport',
    image:
      'https://images.unsplash.com/photo-1616432043562-3671ea2e5242?auto=format&fit=crop&w=900&q=80',
  },
  {
    type: 'Flatbeds',
    capacity: '14 ton steel deck',
    location: 'Durban, KwaZulu-Natal',
    availability: 'Available 22 May',
    pricing: 'R31 / km',
    owner: 'Umgeni Heavy Logistics',
    image:
      'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&w=900&q=80',
  },
  {
    type: 'Abnormal load trucks',
    capacity: 'Lowbed trailer up to 45 ton',
    location: 'Rustenburg, North West',
    availability: 'Pre-book 72 hours',
    pricing: 'R68 / km',
    owner: 'Platinum Heavy Haul',
    image:
      'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?auto=format&fit=crop&w=900&q=80',
  },
];

const MARKETPLACE_METRICS = [
  ['Verified vehicles', '1,248'],
  ['Open requests', '326'],
  ['Tracked rentals', '91'],
];

const CATEGORY_DETAILS = {
  Bakkies: 'Local delivery, spares, light construction loads',
  Vans: 'Retail, parcels, event stock, secure urban movement',
  'Small trucks': 'Palletised loads, branch replenishment, regional trips',
  'Refrigerated trucks': 'Cold chain, produce, medical and FMCG cargo',
  Tankers: 'Fuel, chemicals and compliant liquid bulk transport',
  Flatbeds: 'Steel, timber, machinery and open-deck freight',
  'Abnormal load trucks': 'Heavy equipment, mining and project cargo',
};

const MARKETPLACE_STEPS = [
  ['Search capacity', 'Compare verified vehicle types, regions, availability and rates.'],
  ['Request booking', 'Send a rental or lease request with your preferred route and dates.'],
  ['Move with confidence', 'Use HoneyBadger compliance, tracking and request status visibility.'],
];

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const BOOKING_STATUSES = [
  { label: 'Pending', count: 6, color: '#f59e0b' },
  { label: 'Approved', count: 3, color: '#16a34a' },
  { label: 'Rejected', count: 1, color: '#ef4444' },
  { label: 'Active', count: 2, color: '#0ea5e9' },
  { label: 'Completed', count: 14, color: '#64748b' },
];

const RENTER_NAV_ITEMS = [
  { value: 'dashboard', label: 'Dashboard', icon: FactCheckOutlined },
  { value: 'profile', label: 'My Profile', icon: PersonOutlined },
  { value: 'documents', label: 'My Documents', icon: CloudUploadOutlined },
  { value: 'vehicles', label: 'My Vehicles', icon: DirectionsCarFilledOutlined },
  { value: 'requests', label: 'Lease Requests', icon: TaskAltOutlined },
  { value: 'rates', label: 'Rates & Availability', icon: CalendarMonthOutlined },
  { value: 'jobs', label: 'Jobs', icon: LocalShippingOutlined },
  { value: 'invoices', label: 'Invoices', icon: PaymentsOutlined },
  { value: 'banking', label: 'Banking', icon: BadgeOutlined },
  { value: 'settings', label: 'Settings', icon: SettingsOutlined },
];

const RENTER_SUMMARY_CARDS = [
  { label: 'Profile Completion', value: '80%', helper: '4 of 5 onboarding steps done', progress: 80 },
  { label: 'Vehicles Listed', value: '2', helper: '1 available today' },
  { label: 'Pending Job Requests', value: '4', helper: '2 need approval' },
  { label: 'This Month Earnings', value: 'R18,500', helper: 'R6,200 pending payout' },
  { label: 'Outstanding Invoices', value: '2', helper: '1 overdue' },
];

const RENTER_ACTIONS = [
  'Upload ID document',
  'Add banking details',
  'Approve 2 pending lease requests',
  'Set default daily rate for Toyota Hilux',
];

const RENTER_ONBOARDING_STEPS = [
  ['Complete profile', 'Done'],
  ['Upload documents', 'Pending'],
  ['Add vehicle', 'Done'],
  ['Set rates', 'Pending'],
  ['Add banking', 'Pending'],
  ['Start receiving jobs', 'Locked'],
];

const RENTER_DOCUMENTS = [
  { name: 'ID / Passport', uploadDate: 'Not uploaded', status: 'Pending' },
  { name: 'Proof of address', uploadDate: '20 May 2026', status: 'Verified' },
  { name: 'Driver’s license', uploadDate: '21 May 2026', status: 'Pending' },
  { name: 'Company registration document', uploadDate: 'Not uploaded', status: 'Pending' },
];

const RENTER_VEHICLES = [
  {
    title: 'Toyota Hilux 2021',
    status: 'Available',
    dailyRate: 'R950/day',
    location: 'Sandton',
    documents: '4/5 complete',
    image:
      'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?auto=format&fit=crop&w=900&q=80',
    details: {
      Make: 'Toyota',
      Model: 'Hilux',
      Year: '2021',
      'Registration number': 'GP 48 HB ZA',
      VIN: 'AHTBB3CD901234567',
      Mileage: '82,400 km',
      'Fuel type': 'Diesel',
      Transmission: 'Manual',
      'Seating capacity': '5',
      'Vehicle type': 'Bakkie',
    },
  },
  {
    title: 'Mercedes-Benz Sprinter 2020',
    status: 'Unavailable',
    dailyRate: 'R1,450/day',
    location: 'Midrand',
    documents: '5/5 complete',
    image:
      'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=80',
    details: {
      Make: 'Mercedes-Benz',
      Model: 'Sprinter',
      Year: '2020',
      'Registration number': 'GP 22 VN ZA',
      VIN: 'WDB9066332S123456',
      Mileage: '114,800 km',
      'Fuel type': 'Diesel',
      Transmission: 'Automatic',
      'Seating capacity': '3',
      'Vehicle type': 'Van',
    },
  },
];

const RENTER_REQUESTS = [
  {
    leaser: 'Kopano Retail Logistics',
    vehicle: 'Toyota Hilux 2021',
    dateRange: '24 May - 27 May',
    pickup: 'Sandton',
    earnings: 'R3,800',
    status: 'Pending',
  },
  {
    leaser: 'Maboneng Events',
    vehicle: 'Mercedes-Benz Sprinter 2020',
    dateRange: '28 May - 30 May',
    pickup: 'Midrand',
    earnings: 'R4,350',
    status: 'Pending',
  },
  {
    leaser: 'Northgate Foods',
    vehicle: 'Toyota Hilux 2021',
    dateRange: '2 Jun - 6 Jun',
    pickup: 'Fourways',
    earnings: 'R4,750',
    status: 'Approved',
  },
];

const RENTER_JOBS = [
  {
    id: 'JOB-1042',
    vehicle: 'Toyota Hilux 2021',
    leaser: 'Northgate Foods',
    start: '13 May 2026',
    end: '16 May 2026',
    earned: 'R2,850',
    status: 'Paid',
    invoice: 'INV-2048',
  },
  {
    id: 'JOB-1049',
    vehicle: 'Mercedes-Benz Sprinter 2020',
    leaser: 'City Courier Co.',
    start: '18 May 2026',
    end: '19 May 2026',
    earned: 'R2,900',
    status: 'Awaiting Payment',
    invoice: 'INV-2051',
  },
  {
    id: 'JOB-1050',
    vehicle: 'Toyota Hilux 2021',
    leaser: 'Maboneng Events',
    start: '20 May 2026',
    end: '20 May 2026',
    earned: 'R950',
    status: 'Completed',
    invoice: 'Draft',
  },
];

const RENTER_INVOICES = [
  {
    number: 'INV-2048',
    job: 'JOB-1042',
    leaser: 'Northgate Foods',
    amount: 'R2,850',
    status: 'Paid',
    generated: '16 May 2026',
    due: '23 May 2026',
  },
  {
    number: 'INV-2051',
    job: 'JOB-1049',
    leaser: 'City Courier Co.',
    amount: 'R2,900',
    status: 'Sent',
    generated: '19 May 2026',
    due: '26 May 2026',
  },
  {
    number: 'INV-2052',
    job: 'JOB-1050',
    leaser: 'Maboneng Events',
    amount: 'R950',
    status: 'Draft',
    generated: '20 May 2026',
    due: '27 May 2026',
  },
];

const VEHICLE_PHOTO_LABELS = ['Front', 'Back', 'Left side', 'Right side', 'Interior', 'Dashboard', 'Odometer'];

const USER_TYPES = [
  {
    value: 'renter',
    title: 'Owner',
    description: 'List vehicles, manage availability, approve rental requests, and get paid.',
  },
  {
    value: 'leaser',
    title: 'Renter',
    description: 'Find verified vehicles, compare pricing, and request bookings.',
  },
  {
    value: 'both',
    title: 'Both',
    description: 'Own listed vehicles and rent extra vehicles when demand spikes.',
  },
];

const LOGIN_ROLE_CHOICES = [
  {
    value: 'renter',
    title: 'I own vehicles',
    roleLabel: 'Vehicle owner',
    description: 'List trucks, manage availability, approve bookings, and receive payouts.',
    icon: AddBusinessOutlined,
  },
  {
    value: 'leaser',
    title: 'I need vehicles',
    roleLabel: 'Renter',
    description: 'Find verified vehicles, compare rates, and request bookings.',
    icon: ManageSearchOutlined,
  },
];

const MARKETPLACE_LOGIN_CONTEXT = {
  renter: {
    title: 'Continue as a vehicle owner',
    helperText: 'Use your phone number to list vehicles, manage bookings, rates, banking, and compliance.',
    roleLabel: 'Vehicle owner',
  },
  leaser: {
    title: 'Continue as a renter',
    helperText: 'Use your phone number to search vehicles, request bookings, track rentals, and manage invoices.',
    roleLabel: 'Renter',
  },
  both: {
    title: 'Continue with marketplace access',
    helperText: 'Use your phone number to manage vehicle listings and rental requests.',
    roleLabel: 'Owner and renter',
  },
};

const SADC_COUNTRIES_FALLBACK = [
  { country: 'Angola', country_code: 'AO', phone_code: '+244' },
  { country: 'Botswana', country_code: 'BW', phone_code: '+267' },
  { country: 'Comoros', country_code: 'KM', phone_code: '+269' },
  { country: 'Democratic Republic of the Congo', country_code: 'CD', phone_code: '+243' },
  { country: 'Eswatini', country_code: 'SZ', phone_code: '+268' },
  { country: 'Lesotho', country_code: 'LS', phone_code: '+266' },
  { country: 'Madagascar', country_code: 'MG', phone_code: '+261' },
  { country: 'Malawi', country_code: 'MW', phone_code: '+265' },
  { country: 'Mauritius', country_code: 'MU', phone_code: '+230' },
  { country: 'Mozambique', country_code: 'MZ', phone_code: '+258' },
  { country: 'Namibia', country_code: 'NA', phone_code: '+264' },
  { country: 'Seychelles', country_code: 'SC', phone_code: '+248' },
  { country: 'South Africa', country_code: 'ZA', phone_code: '+27' },
  { country: 'Tanzania', country_code: 'TZ', phone_code: '+255' },
  { country: 'Zambia', country_code: 'ZM', phone_code: '+260' },
  { country: 'Zimbabwe', country_code: 'ZW', phone_code: '+263' },
];

const MARKETPLACE_HERO_IMAGE = '/assets/hb-bg.png';
const MARKETPLACE_ACCESS_TOKEN_KEY = 'marketplace_access_token';
const MARKETPLACE_SESSION_KEY = 'marketplace_session';
const MARKETPLACE_USER_KEY = 'marketplace_user';
const MARKETPLACE_ROLE_KEY = 'marketplace_selected_role';
const MARKETPLACE_VIEW_KEY = 'marketplace_active_view';
const MARKETPLACE_OWNER_SECTION_KEY = 'marketplace_owner_active_section';
const MARKETPLACE_PREFERENCES_KEY = 'marketplace_preferences';
const MARKETPLACE_OWNER_BANK_INFO_KEY = 'marketplace_owner_bank_info';
const MARKETPLACE_OWNER_VEHICLES_KEY = 'marketplace_owner_vehicles';
const marketplaceBaseURL =
  process.env.REACT_APP_MARKETPLACE_API_URL ||
  process.env.REACT_APP_API_URL_NGROK ||
  process.env.REACT_APP_API_URL_LOCAL;
const marketplaceFallbackBaseURL = process.env.REACT_APP_API_URL_LOCAL;

const MarketplaceAuthAPI = axios.create({
  baseURL: marketplaceBaseURL,
  headers: {
    accept: 'application/json',
    'Content-Type': 'application/json',
    'api-secret-key': process.env.REACT_APP_API_SECRET_KEY,
  },
  withCredentials: false,
});

const MarketplaceProtectedAPI = axios.create({
  baseURL: marketplaceBaseURL,
  headers: {
    accept: 'application/json',
    'Content-Type': 'application/json',
    'api-secret-key': process.env.REACT_APP_API_SECRET_KEY,
  },
  withCredentials: false,
});

MarketplaceAuthAPI.interceptors.request.use((config) => {
  if (config.headers) {
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Authorization');
      config.headers.delete('authorization');
    } else {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
  }

  return config;
});

MarketplaceAuthAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.code === 'ERR_NETWORK' &&
      error.config &&
      !error.config.__isRetryRequest &&
      marketplaceFallbackBaseURL &&
      marketplaceFallbackBaseURL !== error.config.baseURL
    ) {
      error.config.__isRetryRequest = true;
      error.config.baseURL = marketplaceFallbackBaseURL;
      return MarketplaceAuthAPI(error.config);
    }

    return Promise.reject(error);
  }
);

MarketplaceProtectedAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.code === 'ERR_NETWORK' &&
      error.config &&
      !error.config.__isRetryRequest &&
      marketplaceFallbackBaseURL &&
      marketplaceFallbackBaseURL !== error.config.baseURL
    ) {
      error.config.__isRetryRequest = true;
      error.config.baseURL = marketplaceFallbackBaseURL;
      return MarketplaceProtectedAPI(error.config);
    }

    return Promise.reject(error);
  }
);

const getMarketplaceAccountType = (type) => {
  const accountTypes = {
    renter: 'OWNER',
    leaser: 'RENTER',
    both: 'BOTH',
  };

  return accountTypes[type] || 'OWNER';
};

const getMarketplaceViewType = (accountType, fallbackType = 'renter') => {
  const accountTypes = {
    OWNER: 'renter',
    RENTER: 'leaser',
    LEASER: 'leaser',
    BOTH: 'both',
  };

  return accountTypes[String(accountType || '').toUpperCase()] || fallbackType;
};

const getMarketplaceRoleLabel = (type) => {
  const labels = {
    renter: 'Owner',
    leaser: 'Renter',
    both: 'Both',
  };

  return labels[type] || 'Owner';
};

const buildMarketplacePhoneNumber = (phoneCode, phoneNumber) => {
  const normalizedPhoneCode = phoneCode || '+27';
  const cleanedPhoneNumber = phoneNumber.trim().replace(/[^\d+]/g, '');

  if (cleanedPhoneNumber.startsWith('+')) {
    return cleanedPhoneNumber;
  }

  if (cleanedPhoneNumber.startsWith('00')) {
    return `+${cleanedPhoneNumber.slice(2)}`;
  }

  return `${normalizedPhoneCode}${cleanedPhoneNumber.replace(/^0+/, '')}`;
};

const maskBankAccount = (bankAccount = '') => {
  const digits = bankAccount.replace(/\D/g, '');

  if (!digits) {
    return 'Account not provided';
  }

  return `**** ${digits.slice(-4)}`;
};

const isEncryptedBankDisplayValue = (value = '') => {
  const text = String(value || '').trim();

  return /^gAAAAA/i.test(text) || (text.length > 64 && /^[A-Za-z0-9_-]+=*$/.test(text));
};

const getSafeBankDisplayValue = (value, fallback = '') => {
  const text = String(value || '').trim();

  if (!text || isEncryptedBankDisplayValue(text)) {
    return fallback;
  }

  return text;
};

const getSafeBankAccountMask = (...values) => {
  const safeValue = values.map((value) => getSafeBankDisplayValue(value)).find(Boolean);

  if (!safeValue) {
    return '';
  }

  const digits = safeValue.replace(/\D/g, '');

  return digits.length >= 4 ? `**** ${digits.slice(-4)}` : safeValue;
};

const normalizeOwnerBankInfo = (bankInfo, fallbackBankInfo = {}) => {
  if (!bankInfo) {
    return null;
  }

  const fallbackBankName = getSafeBankDisplayValue(fallbackBankInfo.bank_name);
  const fallbackBankHolder = getSafeBankDisplayValue(fallbackBankInfo.bank_holder);
  const fallbackBranchCode = getSafeBankDisplayValue(fallbackBankInfo.branch_code);
  const accountMask = getSafeBankAccountMask(
    bankInfo.account_mask,
    bankInfo.bank_account,
    fallbackBankInfo.account_mask,
    fallbackBankInfo.bank_account
  );
  const isVerified = bankInfo.verified ?? bankInfo.is_verified ?? fallbackBankInfo.verified ?? false;

  return {
    ...fallbackBankInfo,
    ...bankInfo,
    bank_name: getSafeBankDisplayValue(bankInfo.bank_name, fallbackBankName),
    bank_holder: getSafeBankDisplayValue(bankInfo.bank_holder, fallbackBankHolder),
    branch_code: getSafeBankDisplayValue(bankInfo.branch_code, fallbackBranchCode),
    bank_account: accountMask,
    account_mask: accountMask,
    verified: Boolean(isVerified),
  };
};

const getOwnerBankSummaryLabel = (bankInfo) => {
  if (!bankInfo) {
    return 'No payout account saved';
  }

  const bankName = getSafeBankDisplayValue(bankInfo.bank_name);
  const accountMask = getSafeBankDisplayValue(bankInfo.account_mask || bankInfo.bank_account);

  if (bankName && accountMask) {
    return `${bankName} ${accountMask}`;
  }

  if (accountMask) {
    return `Payout account ${accountMask}`;
  }

  return bankInfo.id ? 'Payout account saved securely' : 'No payout account saved';
};

const getVehicleImageForType = (vehicleType = '') => {
  const normalizedVehicleType = vehicleType.toLowerCase();
  const listing =
    VEHICLE_LISTINGS.find((vehicle) =>
      normalizedVehicleType.includes(vehicle.type.toLowerCase().replace(/s$/, ''))
    ) || VEHICLE_LISTINGS[0];

  return listing.image;
};

const formatOwnerVehicleRate = (dailyRate) => {
  const amount = Number(dailyRate || 0);

  if (!amount) {
    return 'Rate not set';
  }

  return `R${amount.toLocaleString('en-ZA')}/day`;
};

const getNumberFromMixedValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  const match = String(value).replace(/,/g, '').match(/\d+(\.\d+)?/);
  return match ? match[0] : '';
};

const normalizeOwnerVehicleCard = (vehicle = {}) => {
  const make = vehicle.make || vehicle.details?.Make || '';
  const model = vehicle.model || vehicle.details?.Model || '';
  const year = vehicle.year || getNumberFromMixedValue(vehicle.details?.Year);
  const registrationNumber =
    vehicle.registration_number || vehicle.registrationNumber || vehicle.details?.['Registration number'] || '';
  const vin = vehicle.vin || vehicle.details?.VIN || '';
  const mileageKm = vehicle.mileage_km || vehicle.mileageKm || getNumberFromMixedValue(vehicle.details?.Mileage);
  const fuelType = vehicle.fuel_type || vehicle.fuelType || vehicle.details?.['Fuel type'] || '';
  const transmission = vehicle.transmission || vehicle.details?.Transmission || '';
  const seatingCapacity =
    vehicle.seating_capacity ||
    vehicle.seatingCapacity ||
    getNumberFromMixedValue(vehicle.details?.['Seating capacity']);
  const vehicleType = vehicle.vehicle_type || vehicle.details?.['Vehicle type'] || 'Vehicle';
  const availabilityStatus =
    vehicle.availability_status || vehicle.availabilityStatus || vehicle.status || 'available';
  const status = String(availabilityStatus).toLowerCase() === 'available' ? 'Available' : 'Unavailable';
  const isActiveValue = vehicle.is_active ?? vehicle.isActive ?? true;
  const isActive =
    typeof isActiveValue === 'string' ? isActiveValue.toLowerCase() !== 'false' : Boolean(isActiveValue);
  const dailyRateAmount =
    vehicle.daily_rate || vehicle.dailyRateAmount || getNumberFromMixedValue(vehicle.dailyRate);
  const hasStructuredDailyRate =
    vehicle.daily_rate !== undefined || vehicle.dailyRateAmount !== undefined;
  const apiId =
    vehicle.apiId ||
    vehicle.api_id ||
    vehicle.owner_vehicle_id ||
    vehicle.vehicle_id ||
    vehicle.uuid ||
    (/^\d+$/.test(String(vehicle.id || '')) ? vehicle.id : '');
  const idSource =
    apiId ||
    vehicle.id ||
    registrationNumber ||
    [make, model, year].filter(Boolean).join('-') ||
    vehicleType;

  return {
    id: String(idSource).trim().replace(/\s+/g, '-').toLowerCase(),
    apiId: apiId ? String(apiId) : '',
    make,
    model,
    year,
    registration_number: registrationNumber,
    vin,
    mileage_km: Number(mileageKm || 0),
    fuel_type: fuelType,
    transmission,
    seating_capacity: Number(seatingCapacity || 0),
    vehicle_type: vehicleType,
    availability_status: availabilityStatus,
    is_active: isActive,
    daily_rate: Number(dailyRateAmount || 0),
    title: [make, model, year].filter(Boolean).join(' ') || vehicle.title || vehicleType,
    status,
    isActive: Boolean(isActive),
    dailyRate: hasStructuredDailyRate
      ? formatOwnerVehicleRate(dailyRateAmount)
      : vehicle.dailyRate || formatOwnerVehicleRate(dailyRateAmount),
    location: vehicle.location || 'Location not set',
    image: vehicle.image || getVehicleImageForType(vehicleType),
    details: {
      Make: make || 'Not provided',
      Model: model || 'Not provided',
      Year: String(year || 'Not provided'),
      'Registration number': registrationNumber || 'Not provided',
      VIN: vin || 'Not provided',
      Mileage: mileageKm ? `${Number(mileageKm).toLocaleString('en-ZA')} km` : 'Not provided',
      'Fuel type': fuelType || 'Not provided',
      Transmission: transmission || 'Not provided',
      'Seating capacity': String(seatingCapacity || 'Not provided'),
      'Vehicle type': vehicleType,
      'Listing active': Boolean(isActive) ? 'Yes' : 'No',
    },
  };
};

const getOwnerVehicleFormValues = (vehicle, fallbackValues) => {
  const normalizedVehicle = normalizeOwnerVehicleCard(vehicle);

  return {
    ...fallbackValues,
    make: normalizedVehicle.make || '',
    model: normalizedVehicle.model || '',
    year: String(normalizedVehicle.year || ''),
    registrationNumber: normalizedVehicle.registration_number || '',
    vin: normalizedVehicle.vin || '',
    mileageKm: normalizedVehicle.mileage_km ? String(normalizedVehicle.mileage_km) : '',
    fuelType: normalizedVehicle.fuel_type || fallbackValues.fuelType,
    transmission: normalizedVehicle.transmission || fallbackValues.transmission,
    seatingCapacity: normalizedVehicle.seating_capacity
      ? String(normalizedVehicle.seating_capacity)
      : fallbackValues.seatingCapacity,
    vehicleType: normalizedVehicle.vehicle_type || fallbackValues.vehicleType,
    location: normalizedVehicle.location || '',
    availabilityStatus: normalizedVehicle.availability_status || fallbackValues.availabilityStatus,
    isActive: normalizedVehicle.isActive,
    dailyRate: normalizedVehicle.daily_rate ? String(normalizedVehicle.daily_rate) : '',
  };
};

const getOwnerVehiclesFromResponse = (responseData = {}) => {
  const payload = responseData || {};
  const vehicles =
    (Array.isArray(payload) && payload) ||
    payload.vehicles ||
    payload.owner_vehicles ||
    payload.results ||
    payload.items ||
    payload.data?.vehicles ||
    payload.data?.owner_vehicles ||
    payload.data?.results ||
    payload.data;

  return Array.isArray(vehicles) ? vehicles : [];
};

const getOwnerVehicleProcessSummary = (responseData = {}) => {
  const payload = responseData || {};
  return payload.summary || payload.data?.summary || null;
};

const getOwnerVehicleProcessErrors = (responseData = {}) => {
  const payload = responseData || {};
  const errors = payload.errors || payload.data?.errors || [];
  return Array.isArray(errors) ? errors : [];
};

const getOwnerVehicleProcessMessage = (responseData = {}, fallbackMessage) => {
  const payload = responseData || {};
  return payload.message || payload.data?.message || fallbackMessage;
};

const buildOwnerVehiclePayload = (values, { includeIsActive = true } = {}) => {
  const payload = {
    make: values.make.trim(),
    model: values.model.trim(),
    year: Number(values.year),
    registration_number: values.registrationNumber.trim(),
    vin: values.vin.trim(),
    mileage_km: Number(values.mileageKm),
    fuel_type: values.fuelType.trim(),
    transmission: values.transmission.trim(),
    seating_capacity: Number(values.seatingCapacity),
    vehicle_type: values.vehicleType.trim(),
    location: values.location.trim(),
    availability_status: values.availabilityStatus,
    daily_rate: Number(values.dailyRate),
  };

  if (includeIsActive) {
    payload.is_active = Boolean(values.isActive);
  }

  return payload;
};

const getTemplateFilename = (contentDisposition) => {
  const fallbackName = 'honeybadger-vehicle-template.xlsx';
  const filenameMatch = String(contentDisposition || '').match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);

  return filenameMatch ? decodeURIComponent(filenameMatch[1]) : fallbackName;
};

const downloadBrowserFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getStoredMarketplacePreferences = () => {
  try {
    const storedPreferences = JSON.parse(localStorage.getItem(MARKETPLACE_PREFERENCES_KEY) || '{}');

    return {
      weeklyReports: Boolean(storedPreferences.weeklyReports),
      bookingNotifications: storedPreferences.bookingNotifications !== false,
    };
  } catch (error) {
    console.error('Failed to restore marketplace preferences:', error);
    return {
      weeklyReports: false,
      bookingNotifications: true,
    };
  }
};

const storeMarketplacePreferences = (preferences) => {
  localStorage.setItem(MARKETPLACE_PREFERENCES_KEY, JSON.stringify(preferences));
};

const getStoredOwnerActiveSection = () => {
  const storedSection = localStorage.getItem(MARKETPLACE_OWNER_SECTION_KEY);

  return RENTER_NAV_ITEMS.some((item) => item.value === storedSection) ? storedSection : 'dashboard';
};

const storeOwnerActiveSection = (section) => {
  if (RENTER_NAV_ITEMS.some((item) => item.value === section)) {
    localStorage.setItem(MARKETPLACE_OWNER_SECTION_KEY, section);
  }
};

const getStoredOwnerBankInfo = () => {
  try {
    return normalizeOwnerBankInfo(
      JSON.parse(localStorage.getItem(MARKETPLACE_OWNER_BANK_INFO_KEY) || 'null')
    );
  } catch (error) {
    console.error('Failed to restore owner bank info:', error);
    return null;
  }
};

const storeOwnerBankInfo = (bankInfo) => {
  localStorage.setItem(MARKETPLACE_OWNER_BANK_INFO_KEY, JSON.stringify(normalizeOwnerBankInfo(bankInfo)));
};

const getStoredOwnerVehicles = () => {
  try {
    const storedVehicles = JSON.parse(localStorage.getItem(MARKETPLACE_OWNER_VEHICLES_KEY) || '[]');

    return Array.isArray(storedVehicles) ? storedVehicles.map(normalizeOwnerVehicleCard) : [];
  } catch (error) {
    console.error('Failed to restore owner vehicles:', error);
    return [];
  }
};

const storeOwnerVehicles = (vehicles) => {
  localStorage.setItem(
    MARKETPLACE_OWNER_VEHICLES_KEY,
    JSON.stringify((vehicles || []).map(normalizeOwnerVehicleCard))
  );
};

const getMarketplaceAccessToken = (session) =>
  session?.accessToken || localStorage.getItem(MARKETPLACE_ACCESS_TOKEN_KEY);

const getMarketplaceAuthorizationHeaders = (session) => {
  const accessToken = getMarketplaceAccessToken(session);

  if (!accessToken) {
    throw new Error('Marketplace session token is missing. Please log in again.');
  }

  return {
    Authorization: `Bearer ${accessToken}`,
  };
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseMessage =
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.response?.data?.error;
  return responseMessage || error?.message || fallbackMessage;
};

const buildMarketplaceProfilePayload = (values) => ({
  first_name: values.firstName.trim() || null,
  last_name: values.lastName.trim() || null,
  address: values.address.trim() || null,
  city: values.city.trim() || null,
  email: values.email.trim() || null,
  country: values.country.trim() || null,
  postal_code: values.postalCode.trim() || null,
});

const getMarketplaceProfileFromResponse = (responseData = {}) =>
  responseData.user || responseData.profile || responseData.data || responseData;

const getMarketplaceUserFromResponse = (responseData = {}) => {
  const user = responseData.user || responseData.profile || responseData.data;

  if (user && typeof user === 'object') {
    return user;
  }

  if (responseData.phone_number || responseData.user_uuid || responseData.account_type) {
    return responseData;
  }

  return {};
};

const mergeMarketplaceProfileSession = (session, profilePayload, responseData = {}) => {
  const responseProfile = getMarketplaceProfileFromResponse(responseData);
  const mergedUser = {
    ...(session.user || {}),
    ...profilePayload,
    ...responseProfile,
  };
  const accountType = mergedUser.account_type || getMarketplaceAccountType(session.type);

  return {
    ...session,
    phone: mergedUser.phone_number || session.phone,
    firstName: mergedUser.first_name || session.firstName || '',
    lastName: mergedUser.last_name || session.lastName || '',
    address: mergedUser.address || session.address || '',
    city: mergedUser.city || session.city || '',
    email: mergedUser.email || session.email || '',
    country: mergedUser.country || session.country || '',
    postalCode: mergedUser.postal_code || session.postalCode || '',
    type: getMarketplaceViewType(accountType, session.type || 'renter'),
    user: mergedUser,
  };
};

const clearStoredMarketplaceSession = () => {
  localStorage.removeItem(MARKETPLACE_ACCESS_TOKEN_KEY);
  localStorage.removeItem(MARKETPLACE_SESSION_KEY);
  localStorage.removeItem(MARKETPLACE_USER_KEY);
  localStorage.removeItem(MARKETPLACE_ROLE_KEY);
  localStorage.removeItem(MARKETPLACE_VIEW_KEY);
  localStorage.removeItem(MARKETPLACE_OWNER_SECTION_KEY);
  localStorage.removeItem(MARKETPLACE_PREFERENCES_KEY);
  localStorage.removeItem(MARKETPLACE_OWNER_BANK_INFO_KEY);
  localStorage.removeItem(MARKETPLACE_OWNER_VEHICLES_KEY);
};

const storeMarketplaceSession = (marketplaceSession) => {
  localStorage.setItem(MARKETPLACE_ACCESS_TOKEN_KEY, marketplaceSession.accessToken || '');
  localStorage.setItem(MARKETPLACE_USER_KEY, JSON.stringify(marketplaceSession.user || {}));
  localStorage.setItem(MARKETPLACE_SESSION_KEY, JSON.stringify(marketplaceSession));
  localStorage.setItem(MARKETPLACE_ROLE_KEY, marketplaceSession.type || 'renter');
};

const getStoredMarketplaceSession = () => {
  try {
    const accessToken = localStorage.getItem(MARKETPLACE_ACCESS_TOKEN_KEY);
    const storedSession = JSON.parse(localStorage.getItem(MARKETPLACE_SESSION_KEY) || 'null');
    const storedUser = JSON.parse(localStorage.getItem(MARKETPLACE_USER_KEY) || 'null');
    const user = storedSession?.user || storedUser;

    if (!accessToken || !user?.phone_number) {
      return null;
    }

    return {
      phone: user.phone_number,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      address: user.address || '',
      city: user.city || '',
      email: user.email || '',
      country: user.country || '',
      postalCode: user.postal_code || '',
      type: getMarketplaceViewType(user.account_type, storedSession?.type || 'renter'),
      user,
      accessToken,
      tokenType: storedSession?.tokenType || 'bearer',
    };
  } catch (error) {
    console.error('Failed to restore marketplace session:', error);
    return null;
  }
};

const updateMarketplaceProfile = async (session, profilePayload) => {
  const response = await MarketplaceProtectedAPI.patch('market/mp/profile', profilePayload, {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  const updatedSession = mergeMarketplaceProfileSession(session, profilePayload, response.data);
  storeMarketplaceSession(updatedSession);
  return updatedSession;
};

const getMarketplaceProfile = async (session) => {
  const response = await MarketplaceProtectedAPI.get('market/mp/profile', {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  const updatedSession = mergeMarketplaceProfileSession(session, {}, response.data);
  storeMarketplaceSession(updatedSession);
  return updatedSession;
};

const refreshMarketplaceSession = async (session) => {
  const response = await MarketplaceProtectedAPI.post(
    'market/mp/auth/refresh',
    {},
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );
  const responseUser = getMarketplaceUserFromResponse(response.data || {});
  const refreshedSession = mergeMarketplaceProfileSession(
    {
      ...session,
      accessToken: response.data?.access_token || session.accessToken,
      tokenType: response.data?.token_type || session.tokenType || 'bearer',
    },
    {},
    {
      user: {
        ...(session.user || {}),
        ...responseUser,
      },
    }
  );

  storeMarketplaceSession(refreshedSession);
  return refreshedSession;
};

const updateMarketplaceRole = async (session, type) => {
  const accountType = getMarketplaceAccountType(type);
  const response = await MarketplaceProtectedAPI.patch(
    'market/mp/role',
    {
      account_type: accountType,
    },
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );
  const responseUser = getMarketplaceUserFromResponse(response.data || {});
  const updatedSession = mergeMarketplaceProfileSession(
    session,
    {},
    {
      user: {
        ...(session.user || {}),
        ...responseUser,
        account_type: responseUser.account_type || response.data?.account_type || accountType,
      },
    }
  );

  storeMarketplaceSession(updatedSession);
  return updatedSession;
};

const createOwnerVehicle = async (session, vehiclePayload) => {
  const response = await MarketplaceProtectedAPI.post('market/owner/vehicles', vehiclePayload, {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  return response.data;
};

const downloadOwnerVehicleTemplate = async (session) => {
  const response = await MarketplaceProtectedAPI.get('market/owner/vehicles/template', {
    headers: getMarketplaceAuthorizationHeaders(session),
    responseType: 'blob',
  });

  return {
    blob: response.data,
    filename: getTemplateFilename(response.headers?.['content-disposition']),
  };
};

const getOwnerVehicles = async (session) => {
  const response = await MarketplaceProtectedAPI.get('market/owner/vehicles', {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  return getOwnerVehiclesFromResponse(response.data).map(normalizeOwnerVehicleCard);
};

const uploadOwnerVehicleSpreadsheet = async (session, file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await MarketplaceProtectedAPI.post('market/owner/vehicles/upload', formData, {
    headers: {
      ...getMarketplaceAuthorizationHeaders(session),
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });

  return response.data;
};

const bulkCreateOwnerVehicles = async (session, vehicles) => {
  const response = await MarketplaceProtectedAPI.post(
    'market/owner/vehicles/bulk',
    { vehicles },
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );

  return response.data;
};

const updateOwnerVehicle = async (session, vehicleId, vehiclePayload) => {
  const response = await MarketplaceProtectedAPI.patch(
    `market/owner/vehicles/${vehicleId}`,
    vehiclePayload,
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );

  return response.data;
};

const deleteOwnerVehicle = async (session, vehicleId) => {
  const response = await MarketplaceProtectedAPI.delete(`market/owner/vehicles/${vehicleId}`, {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  return response.data;
};

const getOwnerBankInfo = async (session) => {
  const response = await MarketplaceProtectedAPI.get('market/bank-info/raw', {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  return normalizeOwnerBankInfo(response.data?.data || response.data?.bank_info || response.data);
};

const saveOwnerBankInfo = async (session, bankPayload) => {
  const response = await MarketplaceProtectedAPI.post('market/mp/owner/bank-info', bankPayload, {
    headers: getMarketplaceAuthorizationHeaders(session),
  });

  return response.data;
};

const verifyOwnerBankInfo = async (session, verificationPayload) => {
  const response = await MarketplaceProtectedAPI.post(
    'market/mp/owner/bank-info/verify',
    verificationPayload,
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );

  return response.data;
};

const resendOwnerBankInfoOtp = async (session, bankInfoId) => {
  const response = await MarketplaceProtectedAPI.post(
    `market/bank-info/${bankInfoId}/resend-otp`,
    {},
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );

  return response.data;
};

const logoutMarketplaceSession = async (session) => {
  await MarketplaceProtectedAPI.post(
    'market/mp/logout',
    {},
    {
      headers: getMarketplaceAuthorizationHeaders(session),
    }
  );
};

function MarketplaceHeader() {
  return (
    <Box
      component="header"
      sx={{
        flex: '0 0 auto',
        zIndex: 20,
        borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
        backgroundColor: 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(18px)',
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ py: 1.5 }}
        >
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Box
              component="img"
              src="/assets/images/hb-logo-only-dark.png"
              alt="HoneyBadger"
              sx={{ width: 40, height: 40 }}
            />
            <Box>
              <Typography sx={{ fontWeight: 900, lineHeight: 1, letterSpacing: 0 }}>
                HoneyBadger
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                Marketplace
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button href="/login" color="inherit" sx={{ fontWeight: 800 }}>
              ERP Login
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function HeroMarketplacePanel({ onOpenAuth }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 2,
        maxWidth: 520,
        ml: { md: 'auto' },
        backgroundColor: 'rgba(255,255,255,0.96)',
        color: '#0f172a',
        border: '1px solid rgba(255,255,255,0.8)',
        boxShadow: '0 24px 70px rgba(2, 6, 23, 0.28)',
      }}
    >
      <Stack spacing={2.4}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Find verified capacity
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Search rates and availability before signing in with your phone number.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ManageSearchOutlined />}
            onClick={() => onOpenAuth('leaser')}
            sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0b5f59' } }}
          >
            Find
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddBusinessOutlined />}
            onClick={() => onOpenAuth('renter')}
          >
            List
          </Button>
        </Stack>

        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Vehicle type</InputLabel>
              <Select label="Vehicle type" defaultValue="Refrigerated trucks">
                {VEHICLE_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Province" defaultValue="Gauteng" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Availability" defaultValue="This week" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Preferred rate" defaultValue="Per day" fullWidth />
          </Grid>
        </Grid>

        <Divider />

        <Stack spacing={1.2}>
          {VEHICLE_LISTINGS.slice(0, 3).map((vehicle) => (
            <Stack
              key={vehicle.type}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
            >
              <Box>
                <Typography sx={{ fontWeight: 850 }}>{vehicle.type}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {vehicle.location}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 900 }}>{vehicle.pricing}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

function HeroSection({ onOpenAuth }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: 690 },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        color: '#fff',
        backgroundImage: `linear-gradient(90deg, rgba(7, 20, 35, 0.92) 0%, rgba(7, 28, 48, 0.72) 48%, rgba(7, 20, 35, 0.38) 100%), url("${MARKETPLACE_HERO_IMAGE}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 9 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={3} sx={{ maxWidth: 820 }}>
              <Chip
                icon={<VerifiedUserOutlined />}
                label="Verified logistics vehicle marketplace across South Africa"
                sx={{
                  width: 'fit-content',
                  color: '#d7f7e8',
                  borderColor: 'rgba(215,247,232,0.42)',
                  backgroundColor: 'rgba(10, 91, 67, 0.5)',
                }}
                variant="outlined"
              />
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 42, sm: 58, md: 76 },
                  lineHeight: 0.96,
                  fontWeight: 900,
                  maxWidth: 860,
                }}
              >
                Lease or rent logistics vehicles on demand
              </Typography>
              <Typography sx={{ maxWidth: 650, color: 'rgba(255,255,255,0.84)', fontSize: 18 }}>
                Search capacity, compare rates, verify companies, and request bookings through a
                phone-first marketplace built for South African logistics.
              </Typography>
              <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                {MARKETPLACE_METRICS.map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      px: 1.8,
                      py: 1.1,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.22)',
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>{value}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.76)' }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  size="large"
                  variant="contained"
                  startIcon={<AddBusinessOutlined />}
                  onClick={() => onOpenAuth('renter')}
                  sx={{ py: 1.4, px: 2.5 }}
                >
                  List My Vehicle
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<ManageSearchOutlined />}
                  onClick={() => onOpenAuth('leaser')}
                  sx={{
                    py: 1.4,
                    px: 2.5,
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.72)',
                    '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Find a Vehicle
                </Button>
                <Button
                  size="large"
                  variant="contained"
                  startIcon={<LocalPhoneOutlined />}
                  onClick={() => onOpenAuth()}
                  sx={{
                    py: 1.4,
                    px: 2.5,
                    bgcolor: '#f59e0b',
                    color: '#111827',
                    '&:hover': { bgcolor: '#fbbf24' },
                  }}
                >
                  Login
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <HeroMarketplacePanel onOpenAuth={onOpenAuth} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function CategoryAndTrustSection() {
  return (
    <Box sx={{ backgroundColor: '#f8fafc' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack spacing={4}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'flex-end' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                Built for real logistics jobs
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 720 }}>
                Pick capacity by job type, compare market-friendly rates, and keep every booking
                request tied to HoneyBadger verification.
              </Typography>
            </Box>
            <Chip
              icon={<ShieldOutlined />}
              label="Compliance checked before bookings move"
              sx={{ bgcolor: '#ecfdf5', color: '#0f766e', fontWeight: 800 }}
            />
          </Stack>

          <Grid container spacing={2}>
            {VEHICLE_CATEGORIES.map((category) => (
              <Grid key={category} size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.4,
                    minHeight: 172,
                    height: '100%',
                    borderRadius: 1.5,
                    border: '1px solid #dbe7ef',
                    backgroundColor: '#ffffff',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
                    },
                  }}
                >
                  <Stack spacing={1.4}>
                    <Avatar sx={{ bgcolor: '#e6f4ef', color: '#0f766e' }}>
                      <LocalShippingOutlined />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, mb: 0.6 }}>{category}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {CATEGORY_DETAILS[category]}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            {MARKETPLACE_STEPS.map(([title, description], index) => (
              <Grid key={title} size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderRadius: 1.5,
                    border: '1px solid #dbe7ef',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar sx={{ bgcolor: '#0f172a', color: '#fff', fontWeight: 900 }}>
                      {index + 1}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, mb: 0.4 }}>{title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {description}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

function OtpLoginRegister({ preferredMode, onVerified, phoneInputRef, onChangeRole }) {
  const [step, setStep] = React.useState('phone');
  const [countries, setCountries] = React.useState(SADC_COUNTRIES_FALLBACK);
  const [selectedCountryCode, setSelectedCountryCode] = React.useState('ZA');
  const [phone, setPhone] = React.useState('');
  const [submittedPhoneNumber, setSubmittedPhoneNumber] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [countdown, setCountdown] = React.useState(0);
  const [otpExpiryMinutes, setOtpExpiryMinutes] = React.useState(null);
  const [error, setError] = React.useState('');
  const [countryError, setCountryError] = React.useState('');
  const [isRequestingOtp, setIsRequestingOtp] = React.useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadCountries = async () => {
      try {
        const response = await MarketplaceAuthAPI.get('market/mp/countries/sadc');
        const responseCountries = Array.isArray(response.data?.countries)
          ? response.data.countries
          : [];

        if (!isMounted || responseCountries.length === 0) {
          return;
        }

        setCountries(responseCountries);
        setCountryError('');
        setSelectedCountryCode((currentCountryCode) => {
          if (responseCountries.some((country) => country.country_code === currentCountryCode)) {
            return currentCountryCode;
          }

          return (
            responseCountries.find((country) => country.country_code === 'ZA')?.country_code ||
            responseCountries[0].country_code
          );
        });
      } catch (countriesError) {
        console.error('Failed to load SADC countries:', countriesError);
        if (isMounted) {
          setCountryError('Could not load country codes. Using saved SADC defaults.');
        }
      }
    };

    loadCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (step !== 'otp' || countdown === 0) {
      return undefined;
    }

    const timer = setTimeout(() => setCountdown((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, step]);

  const selectedCountry =
    countries.find((country) => country.country_code === selectedCountryCode) ||
    SADC_COUNTRIES_FALLBACK.find((country) => country.country_code === 'ZA') ||
    SADC_COUNTRIES_FALLBACK[0];
  const loginContext =
    MARKETPLACE_LOGIN_CONTEXT[preferredMode] || MARKETPLACE_LOGIN_CONTEXT.renter;

  const requestOtp = async () => {
    const phoneDigits = phone.trim().replace(/\D/g, '');
    const phoneNumber = buildMarketplacePhoneNumber(selectedCountry?.phone_code, phone);

    if (phoneDigits.length < 7) {
      setError('Enter a valid phone number.');
      return;
    }

    try {
      setError('');
      setIsRequestingOtp(true);

      const response = await MarketplaceAuthAPI.post('market/mp/auth', {
        phone_number: phoneNumber,
        account_type: getMarketplaceAccountType(preferredMode),
      });

      const expiresInMinutes = response.data?.expires_in_minutes || 5;
      setSubmittedPhoneNumber(response.data?.phone_number || phoneNumber);
      setOtpExpiryMinutes(expiresInMinutes);
      setCountdown(expiresInMinutes * 60);
      setStep('otp');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Could not send OTP. Please try again.'));
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.trim().length < 4) {
      setError('Enter the OTP sent to your phone.');
      return;
    }

    try {
      setError('');
      setIsVerifyingOtp(true);

      const phoneNumber =
        submittedPhoneNumber || buildMarketplacePhoneNumber(selectedCountry?.phone_code, phone);
      const response = await MarketplaceAuthAPI.post('market/auth/verify', {
        phone_number: phoneNumber,
        otp: otp.trim(),
      });

      if (!response.data?.authenticated) {
        setError(response.data?.message || 'OTP verification failed.');
        return;
      }

      const verifiedUser = response.data?.user || {};
      const marketplaceSession = {
        phone: verifiedUser.phone_number || phoneNumber,
        firstName: verifiedUser.first_name || '',
        lastName: verifiedUser.last_name || '',
        address: verifiedUser.address || '',
        city: verifiedUser.city || '',
        email: verifiedUser.email || '',
        country: verifiedUser.country || '',
        postalCode: verifiedUser.postal_code || '',
        type: getMarketplaceViewType(verifiedUser.account_type, preferredMode),
        user: verifiedUser,
        accessToken: response.data?.access_token,
        tokenType: response.data?.token_type,
      };

      storeMarketplaceSession(marketplaceSession);
      try {
        const hydratedSession = await getMarketplaceProfile(marketplaceSession);
        onVerified(hydratedSession);
      } catch (profileError) {
        console.error('Failed to fetch marketplace profile after verification:', profileError);
        onVerified(marketplaceSession);
      }
    } catch (verifyError) {
      setError(getApiErrorMessage(verifyError, 'OTP verification failed. Please try again.'));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
      <Stack spacing={2.2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 850 }}>
            {loginContext.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {loginContext.helperText}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 1.25,
            border: '1px solid #fde68a',
            backgroundColor: '#fffbeb',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Box>
              <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 850 }}>
                Marketplace path
              </Typography>
              <Typography sx={{ color: '#111827', fontWeight: 900 }}>
                {loginContext.roleLabel}
              </Typography>
            </Box>
            {step === 'phone' && onChangeRole && (
              <Button size="small" onClick={onChangeRole} sx={{ fontWeight: 850 }}>
                Change
              </Button>
            )}
          </Stack>
        </Box>

        {error && <Alert severity="warning">{error}</Alert>}
        {countryError && step === 'phone' && <Alert severity="info">{countryError}</Alert>}

        {step === 'phone' ? (
          <>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <FormControl fullWidth>
                  <InputLabel>Country code</InputLabel>
                  <Select
                    label="Country code"
                    value={selectedCountryCode}
                    onChange={(event) => {
                      setSelectedCountryCode(event.target.value);
                      setError('');
                    }}
                    renderValue={(countryCode) => {
                      const country = countries.find((item) => item.country_code === countryCode);
                      return country ? `${country.phone_code} ${country.country_code}` : '+27 ZA';
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.country_code} value={country.country_code}>
                        {country.country} ({country.phone_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField
                  label="Phone number"
                  inputRef={phoneInputRef}
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    setError('');
                  }}
                  placeholder="79 685 1164"
                  helperText={
                    phone.trim()
                      ? `Sends as ${buildMarketplacePhoneNumber(selectedCountry?.phone_code, phone)}`
                      : `Selected code: ${selectedCountry?.phone_code} ${selectedCountry?.country}`
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalPhoneOutlined />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Button
              size="large"
              variant="contained"
              onClick={requestOtp}
              disabled={isRequestingOtp}
            >
              {isRequestingOtp ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          </>
        ) : (
          <>
            {otpExpiryMinutes && (
              <Alert severity="success">
                OTP sent to {submittedPhoneNumber}. It expires in {otpExpiryMinutes} minutes.
              </Alert>
            )}
            <TextField
              label="OTP code"
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              placeholder="Enter OTP"
              inputProps={{ maxLength: 6 }}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button size="large" variant="contained" onClick={verifyOtp} disabled={isVerifyingOtp}>
                {isVerifyingOtp ? 'Verifying...' : 'Verify and Continue'}
              </Button>
              <Button
                disabled={countdown > 0 || isRequestingOtp}
                onClick={requestOtp}
              >
                {countdown > 0 ? `Resend OTP in ${formatCountdown(countdown)}` : 'Resend OTP'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function MarketplaceProfileForm({ session, onSaved, submitLabel = 'Save Profile' }) {
  const [profileValues, setProfileValues] = React.useState({
    firstName: session.firstName || '',
    lastName: session.lastName || '',
    address: session.address || session.user?.address || '',
    city: session.city || session.user?.city || '',
    email: session.email || session.user?.email || '',
    country: session.country || session.user?.country || 'South Africa',
    postalCode: session.postalCode || session.user?.postal_code || '',
  });
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setProfileValues({
      firstName: session.firstName || '',
      lastName: session.lastName || '',
      address: session.address || session.user?.address || '',
      city: session.city || session.user?.city || '',
      email: session.email || session.user?.email || '',
      country: session.country || session.user?.country || 'South Africa',
      postalCode: session.postalCode || session.user?.postal_code || '',
    });
  }, [session]);

  const handleProfileChange = (field) => (event) => {
    setError('');
    setSuccess('');
    setProfileValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSaveProfile = async () => {
    if (!profileValues.firstName.trim() || !profileValues.lastName.trim()) {
      setError('Enter your first name and last name to continue.');
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      const updatedSession = await updateMarketplaceProfile(
        session,
        buildMarketplaceProfilePayload(profileValues)
      );

      setSuccess('Profile updated.');
      onSaved(updatedSession);
    } catch (profileError) {
      setError(getApiErrorMessage(profileError, 'Could not update profile. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack spacing={2.2}>
      {error && <Alert severity="warning">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="First name"
            value={profileValues.firstName}
            onChange={handleProfileChange('firstName')}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Last name"
            value={profileValues.lastName}
            onChange={handleProfileChange('lastName')}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Address"
            value={profileValues.address}
            onChange={handleProfileChange('address')}
            placeholder="8 Maude Street, 715 Michaelangelo Towers"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Email"
            type="email"
            value={profileValues.email}
            onChange={handleProfileChange('email')}
            placeholder="name@example.com"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField
            label="City"
            value={profileValues.city}
            onChange={handleProfileChange('city')}
            placeholder="Sandton"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Country"
            value={profileValues.country}
            onChange={handleProfileChange('country')}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Postal code"
            value={profileValues.postalCode}
            onChange={handleProfileChange('postalCode')}
            placeholder="2196"
            fullWidth
          />
        </Grid>
      </Grid>

      <Button
        size="large"
        variant="contained"
        startIcon={<SaveOutlined />}
        onClick={handleSaveProfile}
        disabled={isSaving}
      >
        {isSaving ? 'Saving...' : submitLabel}
      </Button>
    </Stack>
  );
}

function ProfileCompletion({ session, onComplete }) {
  return (
    <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
      <Stack spacing={2.2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 850 }}>
            Complete your profile
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your phone number is verified. Add your profile details before entering the marketplace.
          </Typography>
        </Box>

        <Chip icon={<BadgeOutlined />} label={`Verified phone: ${session.phone}`} color="success" />

        <MarketplaceProfileForm
          session={session}
          onSaved={onComplete}
          submitLabel="Save Profile and Continue"
        />
      </Stack>
    </Paper>
  );
}

function UserTypeSelection({
  selectedType,
  onSelect,
  title = 'Choose marketplace role',
  helperText,
  availableTypes = USER_TYPES,
  disabled = false,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4.5 },
        minHeight: { md: 300 },
        borderRadius: 1.5,
        border: '1px solid #dbe7ef',
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 2.5 }}>
        {title}
      </Typography>
      {helperText && (
        <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
          {helperText}
        </Typography>
      )}
      <Grid container spacing={2}>
        {availableTypes.map((type) => (
          <Grid key={type.value} size={{ xs: 12, md: availableTypes.length === 2 ? 6 : 4 }}>
            <Paper
              elevation={0}
              onClick={() => {
                if (!disabled) {
                  onSelect(type.value);
                }
              }}
              sx={{
                p: { xs: 2.25, md: 2.75 },
                height: '100%',
                minHeight: { md: 150 },
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderRadius: 1.5,
                border:
                  selectedType === type.value ? '2px solid #0f766e' : '1px solid #dbe7ef',
                backgroundColor: selectedType === type.value ? '#ecfdf5' : '#ffffff',
                opacity: disabled ? 0.64 : 1,
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                <TaskAltOutlined
                  color={selectedType === type.value ? 'success' : 'disabled'}
                  sx={{ fontSize: 28 }}
                />
                <Typography sx={{ fontWeight: 850, fontSize: 18 }}>{type.title}</Typography>
              </Stack>
              <Typography sx={{ color: 'text.secondary', fontSize: 14.5, lineHeight: 1.55 }}>
                {type.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function MarketplaceRoleChoice({ selectedType, onSelect }) {
  return (
    <Box sx={{ maxWidth: 860, mx: 'auto' }}>
      <Stack spacing={2.5}>
        <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            What do you want to do?
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 16 }}>
            Choose the path that matches your goal. We will use it to set up the right
            marketplace dashboard after phone verification.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {LOGIN_ROLE_CHOICES.map((choice) => {
            const Icon = choice.icon;
            const isSelected = selectedType === choice.value;

            return (
              <Grid key={choice.value} size={{ xs: 12, md: 6 }}>
                <Paper
                  elevation={0}
                  onClick={() => onSelect(choice.value)}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    height: '100%',
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #0f766e' : '1px solid #dbe7ef',
                    backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                    transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
                    '&:hover': {
                      borderColor: '#0f766e',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                      <Avatar sx={{ bgcolor: '#0f172a', color: '#fff', width: 48, height: 48 }}>
                        <Icon />
                      </Avatar>
                      <Chip
                        size="small"
                        label={choice.roleLabel}
                        sx={{
                          bgcolor: isSelected ? '#ccfbf1' : '#f1f5f9',
                          color: isSelected ? '#0f766e' : '#475569',
                          fontWeight: 850,
                        }}
                      />
                    </Stack>
                    <Box>
                      <Typography sx={{ fontWeight: 900, fontSize: 22, mb: 0.6 }}>
                        {choice.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                        {choice.description}
                      </Typography>
                    </Box>
                    <Button
                      variant={isSelected ? 'contained' : 'outlined'}
                      fullWidth
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(choice.value);
                      }}
                    >
                      Continue
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </Box>
  );
}

function MarketplaceRenterDashboard() {
  return (
    <Grid container spacing={2}>
      {[
        ['Create company profile', 'Add company details, service regions, and contact people.', AddBusinessOutlined],
        ['Upload company documents', 'Attach registration, insurance, and compliance files.', CloudUploadOutlined],
        ['Add vehicles', 'Capture vehicle type, capacity, imagery, and ownership proof.', DirectionsCarFilledOutlined],
        ['Set availability', 'Open dates, maintenance blocks, and depot locations.', CalendarMonthOutlined],
        ['Set pricing', 'Price per day, per trip, per km, or monthly.', PaymentsOutlined],
        ['View booking requests', 'Approve, reject, or negotiate incoming requests.', FactCheckOutlined],
      ].map(([title, description, Icon]) => (
        <Grid key={title} size={{ xs: 12, sm: 6, lg: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
            <Icon sx={{ color: '#0f766e', mb: 1 }} />
            <Typography sx={{ fontWeight: 850, mb: 0.5 }}>{title}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {description}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

function StatusChip({ status }) {
  const statusStyles = {
    Done: { bgcolor: '#ecfdf5', color: '#047857' },
    Verified: { bgcolor: '#ecfdf5', color: '#047857' },
    Available: { bgcolor: '#ecfdf5', color: '#047857' },
    Paid: { bgcolor: '#ecfdf5', color: '#047857' },
    Approved: { bgcolor: '#ecfdf5', color: '#047857' },
    Pending: { bgcolor: '#fffbeb', color: '#b45309' },
    Sent: { bgcolor: '#eff6ff', color: '#1d4ed8' },
    Draft: { bgcolor: '#f1f5f9', color: '#475569' },
    Unavailable: { bgcolor: '#fef2f2', color: '#b91c1c' },
    Rejected: { bgcolor: '#fef2f2', color: '#b91c1c' },
    Locked: { bgcolor: '#f1f5f9', color: '#64748b' },
    'Awaiting Payment': { bgcolor: '#fff7ed', color: '#c2410c' },
    Checking: { bgcolor: '#eff6ff', color: '#1d4ed8' },
    'Not added': { bgcolor: '#fef2f2', color: '#b91c1c' },
  };

  return (
    <Chip
      size="small"
      label={status}
      sx={{
        fontWeight: 850,
        ...(statusStyles[status] || { bgcolor: '#f8fafc', color: '#334155' }),
      }}
    />
  );
}

function RenterSectionShell({ title, subtitle, children, action }) {
  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-end' }}
        spacing={1.5}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: 'text.secondary', mt: 0.4 }}>{subtitle}</Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Stack>
  );
}

function RenterDataTable({ columns, rows, renderActions }) {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key} sx={{ fontWeight: 900, color: '#475569' }}>
                {column.label}
              </TableCell>
            ))}
            {renderActions && <TableCell sx={{ fontWeight: 900, color: '#475569' }}>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id || row.number || row.name || row.leaser} hover>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {column.key === 'status' ? <StatusChip status={row[column.key]} /> : row[column.key]}
                </TableCell>
              ))}
              {renderActions && <TableCell>{renderActions(row)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RenterOverview({ session, onNavigate }) {
  const [bankInfo, setBankInfo] = React.useState(() => getStoredOwnerBankInfo());
  const [isLoadingBankInfo, setIsLoadingBankInfo] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadBankInfo = async () => {
      try {
        setIsLoadingBankInfo(true);
        const loadedBankInfo = await getOwnerBankInfo(session);

        if (!isMounted) {
          return;
        }

        setBankInfo(loadedBankInfo);
        if (loadedBankInfo) {
          storeOwnerBankInfo(loadedBankInfo);
        }
      } catch (bankInfoError) {
        if (!isMounted) {
          return;
        }

        if (bankInfoError?.response?.status === 404) {
          setBankInfo(null);
          localStorage.removeItem(MARKETPLACE_OWNER_BANK_INFO_KEY);
        } else {
          console.error('Failed to check owner bank info:', bankInfoError);
        }
      } finally {
        if (isMounted) {
          setIsLoadingBankInfo(false);
        }
      }
    };

    if (session) {
      loadBankInfo();
    }

    return () => {
      isMounted = false;
    };
  }, [session]);

  const hasBankInfo = Boolean(
    bankInfo?.id ||
      bankInfo?.bank_name ||
      bankInfo?.bank_holder ||
      bankInfo?.branch_code ||
      bankInfo?.account_mask
  );
  const isBankVerified = Boolean(bankInfo?.verified);
  const bankStatus = isLoadingBankInfo && !hasBankInfo
    ? 'Checking'
    : isBankVerified
      ? 'Verified'
      : hasBankInfo
        ? 'Pending'
        : 'Not added';
  const bankingSummaryCard = {
    label: 'Banking Status',
    value: bankStatus,
    helper: isLoadingBankInfo && !hasBankInfo
      ? 'Checking payout account status'
      : isBankVerified
      ? `${getSafeBankDisplayValue(bankInfo?.bank_name, 'Payout account')} is verified for payouts`
      : hasBankInfo
        ? 'Bank details saved. OTP verification is still required'
        : 'Add a payout account before owner payouts can be processed',
  };
  const ownerVehicleCount = getStoredOwnerVehicles().length;
  const summaryCards = [
    ...RENTER_SUMMARY_CARDS.map((card) =>
      card.label === 'Vehicles Listed'
        ? {
            ...card,
            value: String(ownerVehicleCount),
            helper:
              ownerVehicleCount > 0
                ? `${ownerVehicleCount} marketplace vehicle${ownerVehicleCount === 1 ? '' : 's'} loaded`
                : 'No marketplace vehicles loaded yet',
          }
        : card
    ),
    bankingSummaryCard,
  ];
  const bankingAction = isLoadingBankInfo && !hasBankInfo
    ? null
    : isBankVerified
    ? null
    : hasBankInfo
      ? 'Verify banking details with OTP'
      : 'Add banking details';
  const actionItems = [
    ...(bankingAction ? [{ label: bankingAction, isBanking: true }] : []),
    ...RENTER_ACTIONS.filter((action) => action !== 'Add banking details').map((action) => ({
      label: action,
      isBanking: false,
    })),
  ];

  return (
    <RenterSectionShell
      title="Dashboard"
      subtitle="Your readiness, money status, and work that needs attention."
    >
      <Grid container spacing={2}>
        {summaryCards.map((card) => (
          <Grid key={card.label} size={{ xs: 12, sm: 6, lg: 2 }}>
            <Paper elevation={0} sx={{ p: 2.2, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 750 }}>
                {card.label}
              </Typography>
              {card.label === 'Banking Status' ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 1.1 }}>
                  <StatusChip status={card.value} />
                </Stack>
              ) : (
                <Typography variant="h4" sx={{ fontWeight: 950, my: 0.7 }}>
                  {card.value}
                </Typography>
              )}
              {card.progress ? (
                <LinearProgress variant="determinate" value={card.progress} sx={{ height: 8, borderRadius: 1 }} />
              ) : null}
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                {card.helper}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Action Required</Typography>
            <Stack spacing={1.2}>
              {actionItems.map((action) => (
                <Stack
                  key={action.label}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ p: 1.3, borderRadius: 1, bgcolor: '#f8fafc' }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flex: 1 }}>
                    {action.isBanking ? (
                      <PaymentsOutlined sx={{ color: '#f59e0b' }} />
                    ) : (
                      <TaskAltOutlined sx={{ color: '#f59e0b' }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 750 }}>
                      {action.label}
                    </Typography>
                  </Stack>
                  {action.isBanking && (
                    <Button size="small" variant="outlined" onClick={() => onNavigate?.('banking')}>
                      Open Banking
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Onboarding path</Typography>
            <Grid container spacing={1.5}>
              {RENTER_ONBOARDING_STEPS.map(([step, status], index) => {
                const resolvedStatus =
                  step === 'Add banking'
                    ? isBankVerified
                      ? 'Done'
                      : hasBankInfo
                        ? 'Pending'
                        : status
                    : status;

                return (
                  <Grid key={step} size={{ xs: 12, sm: 6 }}>
                    <Stack
                      direction="row"
                      spacing={1.3}
                      alignItems="center"
                      sx={{ p: 1.5, borderRadius: 1, border: '1px solid #e2e8f0' }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#0f172a', fontSize: 14 }}>
                        {index + 1}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                          {step}
                        </Typography>
                        <StatusChip status={resolvedStatus} />
                      </Box>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </RenterSectionShell>
  );
}

function RenterProfile({ session }) {
  return (
    <RenterSectionShell title="My Profile" subtitle="Personal details and marketplace identity verification.">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 900, mb: 2 }}>Personal Details</Typography>
            <Grid container spacing={1.5}>
              {[
                ['First name', session?.firstName || 'Boitumelo'],
                ['Last name', session?.lastName || 'Phetla'],
                ['Phone number', session?.phone || '+27 79 685 1164'],
                ['Email', session?.email || session?.user?.email || 'boitumelo@example.com'],
                ['Address', session?.address || '8 Maude Street, 715 Michaelangelo Towers'],
                ['City', session?.city || 'Sandton'],
                ['Country', session?.country || 'South Africa'],
              ].map(([label, value]) => (
                <Grid key={label} size={{ xs: 12, sm: label === 'Address' ? 12 : 6 }}>
                  <TextField label={label} defaultValue={value} fullWidth />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Verification Status</Typography>
            <StatusChip status="Pending" />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Upload the remaining identification documents before vehicles can receive lease
              requests without manual review.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              {RENTER_DOCUMENTS.map((document) => (
                <Stack key={document.name} direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2">{document.name}</Typography>
                  <StatusChip status={document.status} />
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </RenterSectionShell>
  );
}

function RenterDocuments() {
  return (
    <RenterSectionShell
      title="My Documents"
      subtitle="Track upload status for identity, address, driving, and business documents."
      action={<Button variant="contained" startIcon={<CloudUploadOutlined />}>Upload Document</Button>}
    >
      <RenterDataTable
        columns={[
          { key: 'name', label: 'Document name' },
          { key: 'uploadDate', label: 'Upload date' },
          { key: 'status', label: 'Status' },
        ]}
        rows={RENTER_DOCUMENTS}
        renderActions={() => (
          <Stack direction="row" spacing={1}>
            <Button size="small">View</Button>
            <Button size="small" variant="outlined">Replace</Button>
          </Stack>
        )}
      />
    </RenterSectionShell>
  );
}

function VehicleProcessResult({ result }) {
  if (!result) {
    return null;
  }

  const summaryEntries = Object.entries(result.summary || {});

  return (
    <Stack spacing={1.5}>
      <Alert severity={result.errors?.length ? 'warning' : 'success'}>
        {result.message}
      </Alert>
      {summaryEntries.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {summaryEntries.map(([key, value]) => (
            <Chip
              key={key}
              label={`${key.replace(/_/g, ' ')}: ${value}`}
              sx={{ fontWeight: 850, textTransform: 'capitalize' }}
            />
          ))}
        </Stack>
      )}
      {result.errors?.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #fee2e2', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 900 }}>Row</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Registration</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.errors.map((error, index) => (
                <TableRow key={`${error.row || index}-${error.registration_number || 'error'}`}>
                  <TableCell>{error.row || index + 1}</TableCell>
                  <TableCell>{error.registration_number || '-'}</TableCell>
                  <TableCell>{error.error || error.message || 'Validation failed.'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

function RenterVehicles({ session }) {
  const getEmptyBulkVehicleRow = () => ({
    rowId: `${Date.now()}-${Math.random()}`,
    make: '',
    model: '',
    year: '',
    registrationNumber: '',
    vin: '',
    mileageKm: '',
    fuelType: 'Diesel',
    transmission: 'Manual',
    seatingCapacity: '2',
    vehicleType: 'Bakkies',
    location: '',
    availabilityStatus: 'available',
    dailyRate: '',
  });
  const initialVehicleValues = {
    make: '',
    model: '',
    year: '',
    registrationNumber: '',
    vin: '',
    mileageKm: '',
    fuelType: 'Diesel',
    transmission: 'Manual',
    seatingCapacity: '2',
    vehicleType: 'Bakkies',
    location: '',
    availabilityStatus: 'available',
    isActive: true,
    dailyRate: '',
  };
  const [isAddingVehicle, setIsAddingVehicle] = React.useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = React.useState(false);
  const [editingVehicle, setEditingVehicle] = React.useState(null);
  const [vehicleToDelete, setVehicleToDelete] = React.useState(null);
  const [vehicleValues, setVehicleValues] = React.useState(initialVehicleValues);
  const [ownerVehicles, setOwnerVehicles] = React.useState(() => getStoredOwnerVehicles());
  const [isLoadingVehicles, setIsLoadingVehicles] = React.useState(false);
  const [hasLoadedVehicles, setHasLoadedVehicles] = React.useState(false);
  const [isSavingVehicle, setIsSavingVehicle] = React.useState(false);
  const [isDeletingVehicle, setIsDeletingVehicle] = React.useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = React.useState(false);
  const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadResult, setUploadResult] = React.useState(null);
  const [bulkRows, setBulkRows] = React.useState(() => [getEmptyBulkVehicleRow()]);
  const [bulkDefaults, setBulkDefaults] = React.useState({ location: '', dailyRate: '' });
  const [isSavingBulkVehicles, setIsSavingBulkVehicles] = React.useState(false);
  const [bulkResult, setBulkResult] = React.useState(null);
  const [vehicleError, setVehicleError] = React.useState('');
  const [vehicleSuccess, setVehicleSuccess] = React.useState('');
  const fileInputRef = React.useRef(null);
  const vehicles = ownerVehicles;

  React.useEffect(() => {
    let isMounted = true;

    const loadOwnerVehicles = async () => {
      try {
        setIsLoadingVehicles(true);
        setVehicleError('');
        const loadedVehicles = await getOwnerVehicles(session);

        if (!isMounted) {
          return;
        }

        setOwnerVehicles(loadedVehicles);
        storeOwnerVehicles(loadedVehicles);
        setHasLoadedVehicles(true);
      } catch (loadError) {
        console.error('Failed to load owner vehicles:', loadError);
        if (isMounted) {
          setVehicleError(getApiErrorMessage(loadError, 'Could not load your vehicles. Showing saved vehicles if available.'));
          setHasLoadedVehicles(true);
        }
      } finally {
        if (isMounted) {
          setIsLoadingVehicles(false);
        }
      }
    };

    if (session) {
      loadOwnerVehicles();
    }

    return () => {
      isMounted = false;
    };
  }, [session]);

  const mergeOwnerVehicleCards = (incomingVehicles) => {
    const normalizedVehicles = (incomingVehicles || []).map(normalizeOwnerVehicleCard);

    if (normalizedVehicles.length === 0) {
      return;
    }

    setOwnerVehicles((currentVehicles) => {
      const incomingIds = new Set(normalizedVehicles.map((vehicle) => vehicle.id));
      const nextVehicles = [
        ...normalizedVehicles,
        ...currentVehicles.filter((vehicle) => !incomingIds.has(vehicle.id)),
      ];
      storeOwnerVehicles(nextVehicles);
      return nextVehicles;
    });
  };

  const handleVehicleChange = (field) => (event) => {
    setVehicleValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));
    setVehicleError('');
    setVehicleSuccess('');
  };

  const handleOpenAddVehicle = () => {
    setEditingVehicle(null);
    setVehicleValues(initialVehicleValues);
    setVehicleError('');
    setVehicleSuccess('');
    setIsAddingVehicle(true);
  };

  const handleCancelVehicleForm = () => {
    setEditingVehicle(null);
    setVehicleValues(initialVehicleValues);
    setVehicleError('');
    setIsAddingVehicle(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      setVehicleError('');
      const template = await downloadOwnerVehicleTemplate(session);
      downloadBrowserFile(template.blob, template.filename);
      setVehicleSuccess('Vehicle template downloaded.');
    } catch (templateError) {
      setVehicleError(getApiErrorMessage(templateError, 'Could not download vehicle template.'));
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleSpreadsheetUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!/\.(xlsx|xlsm)$/i.test(file.name)) {
      setVehicleError('Upload a valid .xlsx or .xlsm spreadsheet.');
      event.target.value = '';
      return;
    }

    try {
      setIsUploadingSpreadsheet(true);
      setUploadProgress(0);
      setVehicleError('');
      setVehicleSuccess('');
      setUploadResult(null);
      const response = await uploadOwnerVehicleSpreadsheet(session, file, (progressEvent) => {
        if (!progressEvent.total) {
          return;
        }

        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });
      const uploadedVehicles = getOwnerVehiclesFromResponse(response);
      const summary = getOwnerVehicleProcessSummary(response);
      const errors = getOwnerVehicleProcessErrors(response);

      mergeOwnerVehicleCards(uploadedVehicles);
      setUploadResult({
        message: getOwnerVehicleProcessMessage(response, 'Vehicle spreadsheet upload processed.'),
        summary,
        errors,
      });
      setVehicleSuccess(getOwnerVehicleProcessMessage(response, 'Vehicle spreadsheet upload processed.'));
    } catch (uploadError) {
      setVehicleError(getApiErrorMessage(uploadError, 'Could not upload vehicle spreadsheet.'));
    } finally {
      setIsUploadingSpreadsheet(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  const handleBulkRowChange = (rowId, field) => (event) => {
    const value = event.target.value;

    setBulkRows((currentRows) =>
      currentRows.map((row) => (row.rowId === rowId ? { ...row, [field]: value } : row))
    );
    setBulkResult(null);
    setVehicleError('');
  };

  const handleBulkDefaultChange = (field) => (event) => {
    setBulkDefaults((current) => ({
      ...current,
      [field]: event.target.value,
    }));
    setBulkResult(null);
    setVehicleError('');
  };

  const handleApplyBulkDefaults = () => {
    const hasLocation = Boolean(bulkDefaults.location.trim());
    const hasDailyRate = Boolean(String(bulkDefaults.dailyRate || '').trim());

    if (!hasLocation && !hasDailyRate) {
      setVehicleError('Enter a shared location or daily rate before applying to all rows.');
      return;
    }

    setBulkRows((currentRows) =>
      currentRows.map((row) => ({
        ...row,
        location: hasLocation ? bulkDefaults.location : row.location,
        dailyRate: hasDailyRate ? bulkDefaults.dailyRate : row.dailyRate,
      }))
    );
    setBulkResult(null);
    setVehicleError('');
  };

  const handleCopyPreviousBulkRowValues = (rowId) => {
    setBulkRows((currentRows) => {
      const rowIndex = currentRows.findIndex((row) => row.rowId === rowId);

      if (rowIndex <= 0) {
        return currentRows;
      }

      const previousRow = currentRows[rowIndex - 1];
      return currentRows.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              location: previousRow.location,
              dailyRate: previousRow.dailyRate,
            }
          : row
      );
    });
    setBulkResult(null);
    setVehicleError('');
  };

  const handleAddBulkRow = () => {
    setBulkRows((currentRows) => [
      ...currentRows,
      {
        ...getEmptyBulkVehicleRow(),
        location: bulkDefaults.location,
        dailyRate: bulkDefaults.dailyRate,
      },
    ]);
  };

  const handleRemoveBulkRow = (rowId) => {
    setBulkRows((currentRows) =>
      currentRows.length === 1 ? [getEmptyBulkVehicleRow()] : currentRows.filter((row) => row.rowId !== rowId)
    );
  };

  const handleSaveBulkVehicles = async () => {
    const populatedRows = bulkRows.filter((row) =>
      [
        row.make,
        row.model,
        row.year,
        row.registrationNumber,
        row.vin,
        row.mileageKm,
        row.vehicleType,
        row.location,
        row.dailyRate,
      ].some((value) => String(value || '').trim())
    );

    if (populatedRows.length === 0) {
      setVehicleError('Add at least one completed vehicle row before saving.');
      return;
    }

    try {
      const invalidRequiredRowIndex = populatedRows.findIndex((row) =>
        [
          row.make,
          row.model,
          row.year,
          row.registrationNumber,
          row.vin,
          row.mileageKm,
          row.vehicleType,
          row.location,
          row.dailyRate,
        ].some((value) => !String(value || '').trim())
      );

      if (invalidRequiredRowIndex >= 0) {
        setVehicleError(`Bulk row ${invalidRequiredRowIndex + 1} is missing required values.`);
        return;
      }

      const payloadVehicles = populatedRows.map((row) => buildOwnerVehiclePayload(row, { includeIsActive: false }));
      const invalidRowIndex = payloadVehicles.findIndex((vehicle) =>
        [
          vehicle.year,
          vehicle.mileage_km,
          vehicle.seating_capacity,
          vehicle.daily_rate,
        ].some((value) => Number.isNaN(value))
      );

      if (invalidRowIndex >= 0) {
        setVehicleError(`Bulk row ${invalidRowIndex + 1} has invalid numeric values.`);
        return;
      }

      setIsSavingBulkVehicles(true);
      setVehicleError('');
      setVehicleSuccess('');
      setBulkResult(null);
      const response = await bulkCreateOwnerVehicles(session, payloadVehicles);
      const createdVehicles = getOwnerVehiclesFromResponse(response);
      const summary = getOwnerVehicleProcessSummary(response);
      const errors = getOwnerVehicleProcessErrors(response);

      mergeOwnerVehicleCards(createdVehicles);
      setBulkResult({
        message: getOwnerVehicleProcessMessage(response, 'Bulk vehicle creation processed.'),
        summary,
        errors,
      });
      setVehicleSuccess(getOwnerVehicleProcessMessage(response, 'Bulk vehicle creation processed.'));
      if (!errors.length) {
        setBulkRows([getEmptyBulkVehicleRow()]);
      }
    } catch (bulkError) {
      setVehicleError(getApiErrorMessage(bulkError, 'Could not save bulk vehicles.'));
    } finally {
      setIsSavingBulkVehicles(false);
    }
  };

  const handleEditVehicle = (vehicle) => {
    const normalizedVehicle = normalizeOwnerVehicleCard(vehicle);

    setEditingVehicle(normalizedVehicle);
    setVehicleValues(getOwnerVehicleFormValues(normalizedVehicle, initialVehicleValues));
    setVehicleError('');
    setVehicleSuccess('');
    setIsAddingVehicle(true);
  };

  const handleOpenDeleteVehicle = (vehicle) => {
    setVehicleToDelete(normalizeOwnerVehicleCard(vehicle));
    setVehicleError('');
    setVehicleSuccess('');
  };

  const handleCloseDeleteVehicle = () => {
    if (!isDeletingVehicle) {
      setVehicleToDelete(null);
    }
  };

  const removeVehicleFromState = (vehicleId) => {
    setOwnerVehicles((currentVehicles) => {
      const nextVehicles = currentVehicles.filter((vehicle) => vehicle.id !== vehicleId);
      storeOwnerVehicles(nextVehicles);
      return nextVehicles;
    });
  };

  const handleConfirmDeleteVehicle = async () => {
    if (!vehicleToDelete) {
      return;
    }

    try {
      setIsDeletingVehicle(true);
      setVehicleError('');
      setVehicleSuccess('');

      if (vehicleToDelete.apiId) {
        const response = await deleteOwnerVehicle(session, vehicleToDelete.apiId);
        setVehicleSuccess(response?.message || 'Vehicle deleted successfully.');
      } else {
        setVehicleSuccess('Vehicle removed from the local list.');
      }

      removeVehicleFromState(vehicleToDelete.id);
      if (editingVehicle?.id === vehicleToDelete.id) {
        setEditingVehicle(null);
        setVehicleValues(initialVehicleValues);
        setIsAddingVehicle(false);
      }
      setVehicleToDelete(null);
    } catch (deleteError) {
      setVehicleError(getApiErrorMessage(deleteError, 'Could not delete vehicle. Please try again.'));
    } finally {
      setIsDeletingVehicle(false);
    }
  };

  const handleSaveVehicle = async () => {
    const requiredFields = [
      'make',
      'model',
      'year',
      'registrationNumber',
      'vin',
      'mileageKm',
      'vehicleType',
      'location',
      'dailyRate',
    ];
    const missingField = requiredFields.find((field) => !String(vehicleValues[field] || '').trim());

    if (missingField) {
      setVehicleError('Complete make, model, year, registration, VIN, mileage, vehicle type, location, and daily rate.');
      return;
    }

    const vehiclePayload = buildOwnerVehiclePayload(vehicleValues);

    if (
      [
        vehiclePayload.year,
        vehiclePayload.mileage_km,
        vehiclePayload.seating_capacity,
        vehiclePayload.daily_rate,
      ].some((value) => Number.isNaN(value))
    ) {
      setVehicleError('Year, mileage, seating capacity, and daily rate must be valid numbers.');
      return;
    }

    if (editingVehicle) {
      try {
        setIsSavingVehicle(true);
        setVehicleError('');
        setVehicleSuccess('');

        let responseVehicle = {};
        if (editingVehicle.apiId) {
          const response = await updateOwnerVehicle(session, editingVehicle.apiId, vehiclePayload);
          responseVehicle =
            response?.vehicle || response?.data || response?.owner_vehicle || response?.marketplace_vehicle || {};
        }

        const updatedVehicle = normalizeOwnerVehicleCard({
          ...editingVehicle,
          ...vehiclePayload,
          ...responseVehicle,
          id: editingVehicle.id,
          apiId: editingVehicle.apiId,
          image: editingVehicle.image,
        });

        setOwnerVehicles((currentVehicles) => {
          const nextVehicles = [
            updatedVehicle,
            ...currentVehicles.filter((vehicle) => vehicle.id !== updatedVehicle.id),
          ];
          storeOwnerVehicles(nextVehicles);
          return nextVehicles;
        });
        setVehicleValues(initialVehicleValues);
        setEditingVehicle(null);
        setIsAddingVehicle(false);
        setVehicleSuccess(
          editingVehicle.apiId
            ? 'Vehicle updated successfully.'
            : 'Vehicle card updated locally. Backend vehicle id is not available for this card.'
        );
      } catch (vehicleUpdateError) {
        setVehicleError(getApiErrorMessage(vehicleUpdateError, 'Could not update vehicle. Please try again.'));
      } finally {
        setIsSavingVehicle(false);
      }
      return;
    }

    try {
      setIsSavingVehicle(true);
      setVehicleError('');
      setVehicleSuccess('');
      const response = await createOwnerVehicle(session, vehiclePayload);
      const responseVehicle =
        response?.vehicle || response?.data || response?.owner_vehicle || response?.marketplace_vehicle || {};
      const createdVehicle = normalizeOwnerVehicleCard({
        ...vehiclePayload,
        ...responseVehicle,
      });

      setOwnerVehicles((currentVehicles) => {
        const nextVehicles = [createdVehicle, ...currentVehicles];
        storeOwnerVehicles(nextVehicles);
        return nextVehicles;
      });
      setVehicleValues(initialVehicleValues);
      setIsAddingVehicle(false);
      setVehicleSuccess(response?.message || 'Vehicle added successfully.');
    } catch (vehicleErrorResponse) {
      setVehicleError(getApiErrorMessage(vehicleErrorResponse, 'Could not add vehicle. Please try again.'));
    } finally {
      setIsSavingVehicle(false);
    }
  };

  return (
    <RenterSectionShell
      title="My Vehicles"
      subtitle="Vehicle cards keep photos, rates, listing status, and availability in one place."
      action={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddOutlined />}
            onClick={() => {
              setBulkResult(null);
              setVehicleError('');
              setIsBulkModalOpen(true);
            }}
          >
            Bulk Add Vehicles
          </Button>
          <Button
            variant="contained"
            startIcon={<DirectionsCarFilledOutlined />}
            onClick={isAddingVehicle ? handleCancelVehicleForm : handleOpenAddVehicle}
          >
            {isAddingVehicle ? 'Close Form' : 'Add Vehicle'}
          </Button>
        </Stack>
      }
    >
      {vehicleError && <Alert severity="warning">{vehicleError}</Alert>}
      {vehicleSuccess && <Alert severity="success">{vehicleSuccess}</Alert>}
      {isLoadingVehicles && <LinearProgress sx={{ borderRadius: 1 }} />}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>Excel Bulk Upload</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Download the HoneyBadger template, complete it offline, then upload the filled spreadsheet.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlined />}
                  onClick={handleDownloadTemplate}
                  disabled={isDownloadingTemplate}
                >
                  {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  hidden
                  onChange={handleSpreadsheetUpload}
                />
                <Button
                  variant="contained"
                  startIcon={<CloudUploadOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingSpreadsheet}
                >
                  {isUploadingSpreadsheet ? 'Uploading...' : 'Upload Excel'}
                </Button>
              </Stack>
              {isUploadingSpreadsheet && (
                <LinearProgress
                  variant={uploadProgress ? 'determinate' : 'indeterminate'}
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              )}
              <VehicleProcessResult result={uploadResult} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Bulk Add Vehicles
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Add vehicle rows directly. Fields wrap into grouped rows so the form stays readable without horizontal scrolling.
            </Typography>
            {vehicleError && <Alert severity="warning">{vehicleError}</Alert>}
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #dbe7ef', borderRadius: 1.5, bgcolor: '#f8fafc' }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>Shared values</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Use this when multiple vehicles share the same location or daily rate.
                  </Typography>
                </Box>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      label="Shared location"
                      value={bulkDefaults.location}
                      onChange={handleBulkDefaultChange('location')}
                      placeholder="Sandton, Gauteng"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      label="Shared daily rate"
                      type="number"
                      value={bulkDefaults.dailyRate}
                      onChange={handleBulkDefaultChange('dailyRate')}
                      placeholder="950"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Button variant="contained" onClick={handleApplyBulkDefaults} fullWidth>
                      Apply to all rows
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
            <Stack spacing={2}>
              {bulkRows.map((row, index) => (
                <Paper
                  key={row.rowId}
                  elevation={0}
                  sx={{ p: 2, border: '1px solid #dbe7ef', borderRadius: 1.5 }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography sx={{ fontWeight: 900 }}>Vehicle row {index + 1}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {index > 0 && (
                          <Button size="small" variant="outlined" onClick={() => handleCopyPreviousBulkRowValues(row.rowId)}>
                            Copy previous location/rate
                          </Button>
                        )}
                        <IconButton aria-label="Remove row" onClick={() => handleRemoveBulkRow(row.rowId)}>
                          <DeleteOutlineOutlined />
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <TextField label="Make" value={row.make} onChange={handleBulkRowChange(row.rowId, 'make')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <TextField label="Model" value={row.model} onChange={handleBulkRowChange(row.rowId, 'model')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <TextField label="Year" type="number" value={row.year} onChange={handleBulkRowChange(row.rowId, 'year')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                        <TextField label="Registration number" value={row.registrationNumber} onChange={handleBulkRowChange(row.rowId, 'registrationNumber')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                        <TextField label="VIN" value={row.vin} onChange={handleBulkRowChange(row.rowId, 'vin')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <TextField label="Mileage km" type="number" value={row.mileageKm} onChange={handleBulkRowChange(row.rowId, 'mileageKm')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <TextField label="Fuel type" select value={row.fuelType} onChange={handleBulkRowChange(row.rowId, 'fuelType')} fullWidth>
                          {['Diesel', 'Petrol', 'Electric', 'Hybrid'].map((fuelType) => (
                            <MenuItem key={fuelType} value={fuelType}>{fuelType}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <TextField label="Transmission" select value={row.transmission} onChange={handleBulkRowChange(row.rowId, 'transmission')} fullWidth>
                          {['Manual', 'Automatic'].map((transmission) => (
                            <MenuItem key={transmission} value={transmission}>{transmission}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <TextField label="Seats" type="number" value={row.seatingCapacity} onChange={handleBulkRowChange(row.rowId, 'seatingCapacity')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                        <TextField label="Vehicle type" select value={row.vehicleType} onChange={handleBulkRowChange(row.rowId, 'vehicleType')} fullWidth>
                          {VEHICLE_CATEGORIES.map((category) => (
                            <MenuItem key={category} value={category}>{category}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                        <TextField label="Location" value={row.location} onChange={handleBulkRowChange(row.rowId, 'location')} fullWidth />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <TextField label="Availability" select value={row.availabilityStatus} onChange={handleBulkRowChange(row.rowId, 'availabilityStatus')} fullWidth>
                          <MenuItem value="available">Available</MenuItem>
                          <MenuItem value="unavailable">Unavailable</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <TextField label="Daily rate" type="number" value={row.dailyRate} onChange={handleBulkRowChange(row.rowId, 'dailyRate')} fullWidth />
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" startIcon={<AddOutlined />} onClick={handleAddBulkRow}>
                Add Row
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={handleSaveBulkVehicles}
                disabled={isSavingBulkVehicles}
              >
                {isSavingBulkVehicles ? 'Saving Rows...' : 'Save All Rows'}
              </Button>
              <Button variant="text" onClick={() => setIsBulkModalOpen(false)}>
                Close
              </Button>
            </Stack>
            <VehicleProcessResult result={bulkResult} />
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(vehicleToDelete)}
        onClose={handleCloseDeleteVehicle}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Confirm vehicle deletion
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: '#fef2f2', color: '#b91c1c', width: 48, height: 48 }}>
                <WarningAmberOutlined />
              </Avatar>
              <Avatar sx={{ bgcolor: '#fff7ed', color: '#c2410c', width: 48, height: 48 }}>
                <DirectionsCarFilledOutlined />
              </Avatar>
              <Avatar sx={{ bgcolor: '#fee2e2', color: '#dc2626', width: 48, height: 48 }}>
                <DeleteOutlineOutlined />
              </Avatar>
            </Stack>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 0.7 }}>
                Delete {vehicleToDelete?.title || 'this vehicle'}?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                This removes the vehicle from the owner marketplace inventory. Booking and listing visibility for this vehicle will stop.
              </Typography>
            </Box>
            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #fee2e2', borderRadius: 1.5, bgcolor: '#fff7f7' }}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Registration</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                    {vehicleToDelete?.registration_number || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Location</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                    {vehicleToDelete?.location || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Daily rate</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                    {vehicleToDelete?.dailyRate || 'Not set'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Backend ID</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                    {vehicleToDelete?.apiId || 'Local only'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteVehicle} disabled={isDeletingVehicle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineOutlined />}
            onClick={handleConfirmDeleteVehicle}
            disabled={isDeletingVehicle}
          >
            {isDeletingVehicle ? 'Deleting...' : 'Delete Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      {isAddingVehicle && (
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 900 }}>
                {editingVehicle ? 'Edit Owner Vehicle' : 'Add Owner Vehicle'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {editingVehicle
                  ? 'Update the vehicle card details shown in the owner marketplace dashboard.'
                  : 'Capture the vehicle details required for marketplace listing and compliance checks.'}
              </Typography>
            </Box>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Make" value={vehicleValues.make} onChange={handleVehicleChange('make')} placeholder="Toyota" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Model" value={vehicleValues.model} onChange={handleVehicleChange('model')} placeholder="Hilux" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Year" value={vehicleValues.year} onChange={handleVehicleChange('year')} placeholder="2021" type="number" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Registration number"
                  value={vehicleValues.registrationNumber}
                  onChange={handleVehicleChange('registrationNumber')}
                  placeholder="GP 48 HB ZA"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="VIN" value={vehicleValues.vin} onChange={handleVehicleChange('vin')} placeholder="AHTBB3CD901234567" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Mileage km" value={vehicleValues.mileageKm} onChange={handleVehicleChange('mileageKm')} placeholder="82400" type="number" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Fuel type" value={vehicleValues.fuelType} onChange={handleVehicleChange('fuelType')} select fullWidth>
                  {['Diesel', 'Petrol', 'Electric', 'Hybrid'].map((fuelType) => (
                    <MenuItem key={fuelType} value={fuelType}>
                      {fuelType}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Transmission" value={vehicleValues.transmission} onChange={handleVehicleChange('transmission')} select fullWidth>
                  {['Manual', 'Automatic'].map((transmission) => (
                    <MenuItem key={transmission} value={transmission}>
                      {transmission}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Seating capacity"
                  value={vehicleValues.seatingCapacity}
                  onChange={handleVehicleChange('seatingCapacity')}
                  type="number"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField label="Vehicle type" value={vehicleValues.vehicleType} onChange={handleVehicleChange('vehicleType')} select fullWidth>
                  {VEHICLE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Availability"
                  value={vehicleValues.availabilityStatus}
                  onChange={handleVehicleChange('availabilityStatus')}
                  select
                  fullWidth
                >
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="unavailable">Unavailable</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={vehicleValues.isActive}
                      onChange={(event) => {
                        setVehicleValues((current) => ({
                          ...current,
                          isActive: event.target.checked,
                        }));
                        setVehicleError('');
                        setVehicleSuccess('');
                      }}
                    />
                  }
                  label="Active listing"
                  sx={{
                    minHeight: 56,
                    m: 0,
                    px: 1.5,
                    border: '1px solid #dbe7ef',
                    borderRadius: 1,
                    width: '100%',
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Location" value={vehicleValues.location} onChange={handleVehicleChange('location')} placeholder="Sandton, Gauteng" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Daily rate" value={vehicleValues.dailyRate} onChange={handleVehicleChange('dailyRate')} placeholder="950" type="number" fullWidth />
              </Grid>
            </Grid>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="contained" startIcon={<SaveOutlined />} onClick={handleSaveVehicle} disabled={isSavingVehicle}>
                {isSavingVehicle
                  ? editingVehicle
                    ? 'Saving Changes...'
                    : 'Saving Vehicle...'
                  : editingVehicle
                    ? 'Save Changes'
                    : 'Save Vehicle'}
              </Button>
              <Button variant="outlined" onClick={handleCancelVehicleForm}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {hasLoadedVehicles && !isLoadingVehicles && vehicles.length === 0 && !isAddingVehicle && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
          <Stack spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} textAlign={{ sm: 'center' }}>
            <DirectionsCarFilledOutlined sx={{ color: '#f59e0b', fontSize: 36 }} />
            <Box>
              <Typography sx={{ fontWeight: 900 }}>No vehicles listed yet</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Add your first marketplace vehicle to make it available for renter requests.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<DirectionsCarFilledOutlined />} onClick={handleOpenAddVehicle}>
              Add Vehicle
            </Button>
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        {vehicles.map((vehicle) => (
          <Grid key={vehicle.id || vehicle.title} size={{ xs: 12, lg: 6 }}>
            <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
              <Box component="img" src={vehicle.image} alt={vehicle.title} sx={{ width: '100%', height: 220, objectFit: 'cover' }} />
              <Stack spacing={2} sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{vehicle.title}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{vehicle.location}</Typography>
                  </Box>
                  <Stack spacing={1} alignItems="flex-end">
                    <StatusChip status={vehicle.status} />
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditOutlined />}
                        onClick={() => handleEditVehicle(vehicle)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineOutlined />}
                        onClick={() => handleOpenDeleteVehicle(vehicle)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label={`Daily rate: ${vehicle.dailyRate}`} sx={{ fontWeight: 800 }} />
                  <Chip
                    label={vehicle.isActive ? 'Active listing' : 'Inactive listing'}
                    color={vehicle.isActive ? 'success' : 'default'}
                    variant={vehicle.isActive ? 'filled' : 'outlined'}
                  />
                </Stack>
                <Grid container spacing={1}>
                  {Object.entries(vehicle.details).map(([label, value]) => (
                    <Grid key={label} size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider />
                <Box>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>Photos</Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {VEHICLE_PHOTO_LABELS.map((label) => <Chip key={label} label={label} size="small" />)}
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </RenterSectionShell>
  );
}

function RenterRequests() {
  return (
    <RenterSectionShell title="Lease Requests" subtitle="Review interested renters and approve jobs with confidence.">
      <RenterDataTable
        columns={[
          { key: 'leaser', label: 'Renter' },
          { key: 'vehicle', label: 'Vehicle Requested' },
          { key: 'dateRange', label: 'Date Range' },
          { key: 'pickup', label: 'Pickup Location' },
          { key: 'earnings', label: 'Estimated Earnings' },
          { key: 'status', label: 'Status' },
        ]}
        rows={RENTER_REQUESTS}
        renderActions={(row) => (
          <Stack direction="row" spacing={1}>
            <Button size="small">View</Button>
            <Button size="small" variant="contained" disabled={row.status !== 'Pending'}>Approve</Button>
            <Button size="small" color="error" disabled={row.status !== 'Pending'}>Reject</Button>
            <Button size="small" variant="outlined">Message</Button>
          </Stack>
        )}
      />
    </RenterSectionShell>
  );
}

function RenterRatesAvailability() {
  return (
    <RenterSectionShell title="Rates & Availability" subtitle="Set base pricing, discounts, deposits, and blocked dates.">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Base Rate</Typography>
            <Typography variant="h3" sx={{ fontWeight: 950 }}>R950</Typography>
            <Typography sx={{ color: 'text.secondary' }}>/ day</Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              <Chip label="7+ days: 10% off" />
              <Chip label="30+ days: 20% off" />
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Grid container spacing={1.5}>
              {[
                ['Default daily rate', '950'],
                ['Weekly rate', '5,985'],
                ['Monthly rate', '22,800'],
                ['Deposit required', '2,000'],
                ['Minimum rental duration', '1 day'],
                ['Delivery fee', '350'],
                ['Late return penalty', '450'],
              ].map(([label, value]) => (
                <Grid key={label} size={{ xs: 12, sm: 6 }}>
                  <TextField label={label} defaultValue={value} fullWidth />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <StatusChip status="Available" />
              <Chip label="Blocked: 26 May" variant="outlined" />
              <Chip label="Blocked: 11 Jun - 13 Jun" variant="outlined" />
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </RenterSectionShell>
  );
}

function RenterJobs() {
  return (
    <RenterSectionShell title="Jobs" subtitle="Previous and active jobs accepted from lease requests.">
      <RenterDataTable
        columns={[
          { key: 'id', label: 'Job ID' },
          { key: 'vehicle', label: 'Vehicle' },
          { key: 'leaser', label: 'Renter' },
          { key: 'start', label: 'Start Date' },
          { key: 'end', label: 'End Date' },
          { key: 'earned', label: 'Total Earned' },
          { key: 'status', label: 'Status' },
          { key: 'invoice', label: 'Invoice' },
        ]}
        rows={RENTER_JOBS}
      />
    </RenterSectionShell>
  );
}

function RenterInvoices() {
  return (
    <RenterSectionShell title="Invoices" subtitle="Generate, send, download, and reconcile job invoices.">
      <RenterDataTable
        columns={[
          { key: 'number', label: 'Invoice Number' },
          { key: 'job', label: 'Job' },
          { key: 'leaser', label: 'Renter' },
          { key: 'amount', label: 'Amount' },
          { key: 'status', label: 'Status' },
          { key: 'generated', label: 'Generated Date' },
          { key: 'due', label: 'Due Date' },
        ]}
        rows={RENTER_INVOICES}
        renderActions={() => (
          <Stack direction="row" spacing={1}>
            <Button size="small">View</Button>
            <Button size="small" variant="outlined">Download PDF</Button>
            <Button size="small" variant="outlined">Send</Button>
            <Button size="small">Mark Paid</Button>
          </Stack>
        )}
      />
    </RenterSectionShell>
  );
}

function RenterBanking() {
  return (
    <RenterSectionShell title="Banking" subtitle="Secure payout account management with masked details by default.">
      <Paper elevation={0} sx={{ p: 2.5, maxWidth: 620, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>Payout Account</Typography>
        <Stack spacing={1.2}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography sx={{ fontWeight: 850 }}>FNB •••• 4721</Typography>
            <StatusChip status="Verified" />
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Account holder: Boitumelo Phetla</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Account type: Current account</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Branch code: 250655</Typography>
          <Divider />
          <Button variant="outlined" startIcon={<PaymentsOutlined />} sx={{ width: 'fit-content' }}>
            Edit Banking Details
          </Button>
        </Stack>
      </Paper>
    </RenterSectionShell>
  );
}

function OwnerBanking({ session }) {
  const storedBankInfo = React.useMemo(() => getStoredOwnerBankInfo(), []);
  const [bankValues, setBankValues] = React.useState({
    bankAccount: '',
    bankName: storedBankInfo?.bank_name || '',
    bankHolder:
      storedBankInfo?.bank_holder ||
      `${session?.firstName || ''} ${session?.lastName || ''}`.trim(),
    branchCode: storedBankInfo?.branch_code || '',
  });
  const [bankInfo, setBankInfo] = React.useState(storedBankInfo);
  const [pendingBankInfoId, setPendingBankInfoId] = React.useState(null);
  const [otp, setOtp] = React.useState('');
  const [countdown, setCountdown] = React.useState(0);
  const [expiresInMinutes, setExpiresInMinutes] = React.useState(null);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [isLoadingBankInfo, setIsLoadingBankInfo] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isResendingOtp, setIsResendingOtp] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadOwnerBankInfo = async () => {
      try {
        setIsLoadingBankInfo(true);
        const loadedBankInfo = await getOwnerBankInfo(session);

        if (!isMounted || !loadedBankInfo) {
          return;
        }

        setBankInfo(loadedBankInfo);
        setBankValues((current) => ({
          ...current,
          bankName: loadedBankInfo.bank_name || current.bankName,
          bankHolder: loadedBankInfo.bank_holder || current.bankHolder,
          branchCode: loadedBankInfo.branch_code || current.branchCode,
        }));
        storeOwnerBankInfo(loadedBankInfo);
      } catch (bankInfoError) {
        console.error('Failed to load owner bank info:', bankInfoError);
        if (isMounted && bankInfoError?.response?.status !== 404) {
          setError(getApiErrorMessage(bankInfoError, 'Could not load banking information.'));
        }
      } finally {
        if (isMounted) {
          setIsLoadingBankInfo(false);
        }
      }
    };

    if (session) {
      loadOwnerBankInfo();
    }

    return () => {
      isMounted = false;
    };
  }, [session]);

  React.useEffect(() => {
    if (countdown === 0) {
      return undefined;
    }

    const timer = setTimeout(() => setCountdown((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleBankChange = (field) => (event) => {
    setError('');
    setSuccess('');
    setBankValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSaveBankInfo = async () => {
    if (
      !bankValues.bankAccount.trim() ||
      !bankValues.bankName.trim() ||
      !bankValues.bankHolder.trim() ||
      !bankValues.branchCode.trim()
    ) {
      setError('Enter bank account, bank name, account holder, and branch code.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setIsSaving(true);
      const response = await saveOwnerBankInfo(session, {
        bank_account: bankValues.bankAccount.trim(),
        bank_name: bankValues.bankName.trim(),
        bank_holder: bankValues.bankHolder.trim(),
        branch_code: bankValues.branchCode.trim(),
      });
      const savedBankInfo = normalizeOwnerBankInfo(response.bank_info || {}, {
        bank_name: bankValues.bankName.trim(),
        bank_holder: bankValues.bankHolder.trim(),
        branch_code: bankValues.branchCode.trim(),
        account_mask: maskBankAccount(bankValues.bankAccount),
      });

      setBankInfo(savedBankInfo);
      setPendingBankInfoId(savedBankInfo.id);
      setExpiresInMinutes(response.expires_in_minutes || 5);
      setCountdown((response.expires_in_minutes || 5) * 60);
      setOtp('');
      setSuccess(response.message || 'Bank information saved. Enter the OTP to verify it.');
      storeOwnerBankInfo(savedBankInfo);
      setBankValues((current) => ({
        ...current,
        bankAccount: '',
      }));
    } catch (bankError) {
      setError(getApiErrorMessage(bankError, 'Could not save banking information. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerifyBankInfo = async () => {
    if (!pendingBankInfoId && !bankInfo?.id) {
      setError('Save banking information before verifying.');
      return;
    }

    if (otp.trim().length < 4) {
      setError('Enter the OTP sent for banking verification.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setIsVerifying(true);
      const response = await verifyOwnerBankInfo(session, {
        bank_info_id: pendingBankInfoId || bankInfo.id,
        otp: otp.trim(),
      });
      const verifiedBankInfo = {
        ...normalizeOwnerBankInfo(response.bank_info || {}, bankInfo || {}),
        account_mask: bankInfo?.account_mask || 'Verified account',
      };

      setBankInfo(verifiedBankInfo);
      setPendingBankInfoId(null);
      setCountdown(0);
      setOtp('');
      setSuccess(response.message || 'Bank information verified successfully.');
      storeOwnerBankInfo(verifiedBankInfo);
    } catch (verifyError) {
      setError(getApiErrorMessage(verifyError, 'Could not verify banking information. Please try again.'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendBankOtp = async () => {
    const bankInfoId = pendingBankInfoId || bankInfo?.id;

    if (!bankInfoId) {
      setError('Save banking information before requesting a new OTP.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setIsResendingOtp(true);
      const response = await resendOwnerBankInfoOtp(session, bankInfoId);
      const nextExpiryMinutes = response.expires_in_minutes || expiresInMinutes || 5;

      setExpiresInMinutes(nextExpiryMinutes);
      setCountdown(nextExpiryMinutes * 60);
      setOtp('');
      setSuccess(response.message || 'A new banking OTP has been sent.');
    } catch (resendError) {
      const message = getApiErrorMessage(resendError, 'Could not resend banking OTP. Please try again.');

      if (message.toLowerCase().includes('already verified')) {
        const verifiedBankInfo = normalizeOwnerBankInfo(
          {
            ...(bankInfo || {}),
            verified: true,
            is_verified: true,
          },
          bankInfo || {}
        );

        setBankInfo(verifiedBankInfo);
        setPendingBankInfoId(null);
        setCountdown(0);
        setSuccess(message);
        storeOwnerBankInfo(verifiedBankInfo);
      } else {
        setError(message);
      }
    } finally {
      setIsResendingOtp(false);
    }
  };

  return (
    <RenterSectionShell title="Banking" subtitle="Secure payout account management with OTP verification.">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Payout Account</Typography>
            <Stack spacing={1.2}>
              {isLoadingBankInfo && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Loading saved banking information...
                </Typography>
              )}
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography sx={{ fontWeight: 850 }}>
                  {getOwnerBankSummaryLabel(bankInfo)}
                </Typography>
                <StatusChip status={bankInfo?.verified ? 'Verified' : 'Pending'} />
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Account holder: {getSafeBankDisplayValue(
                  bankInfo?.bank_holder,
                  bankInfo?.id ? 'Saved securely' : 'Not provided'
                )}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Branch code: {getSafeBankDisplayValue(
                  bankInfo?.branch_code,
                  bankInfo?.id ? 'Saved securely' : 'Not provided'
                )}
              </Typography>
              {bankInfo?.updated_at && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Last updated: {new Date(bankInfo.updated_at).toLocaleString()}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Verification: {bankInfo?.verified ? 'Verified for payouts' : 'OTP verification required before payouts'}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>Edit Banking Details</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Saving bank details sends an OTP to verify the payout account.
                </Typography>
              </Box>

              {error && <Alert severity="warning">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Bank account number"
                    value={bankValues.bankAccount}
                    onChange={handleBankChange('bankAccount')}
                    placeholder="011097175"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Bank name"
                    value={bankValues.bankName}
                    onChange={handleBankChange('bankName')}
                    placeholder="Standard Bank"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 7 }}>
                  <TextField
                    label="Account holder"
                    value={bankValues.bankHolder}
                    onChange={handleBankChange('bankHolder')}
                    placeholder="Boitumelo Phetla"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    label="Branch code"
                    value={bankValues.branchCode}
                    onChange={handleBankChange('branchCode')}
                    placeholder="50058"
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                startIcon={<PaymentsOutlined />}
                onClick={handleSaveBankInfo}
                disabled={isSaving}
                sx={{ width: 'fit-content' }}
              >
                {isSaving ? 'Saving...' : 'Save and Send OTP'}
              </Button>

              {(pendingBankInfoId || (bankInfo?.id && !bankInfo?.verified)) && (
                <>
                  <Divider />
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>Verify Banking OTP</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {expiresInMinutes
                          ? `OTP expires in ${expiresInMinutes} minutes.`
                          : 'Enter the OTP sent for banking verification.'}
                      </Typography>
                    </Box>
                    <TextField
                      label="Banking OTP"
                      value={otp}
                      onChange={(event) => {
                        setOtp(event.target.value);
                        setError('');
                      }}
                      inputProps={{ maxLength: 6 }}
                      placeholder="194529"
                      fullWidth
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Button variant="contained" onClick={handleVerifyBankInfo} disabled={isVerifying}>
                        {isVerifying ? 'Verifying...' : 'Verify Banking Details'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleResendBankOtp}
                        disabled={countdown > 0 || isResendingOtp}
                      >
                        {isResendingOtp
                          ? 'Sending...'
                          : countdown > 0
                            ? `Resend OTP in ${formatCountdown(countdown)}`
                            : 'Resend OTP'}
                      </Button>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </RenterSectionShell>
  );
}

function RenterSettings() {
  return (
    <RenterSectionShell title="Settings" subtitle="Marketplace notification, approval, and payout preferences.">
      <Grid container spacing={2}>
        {[
          ['Request notifications', 'SMS and email alerts for every new lease request.'],
          ['Auto-approval rules', 'Keep manual approval on for new renters until verification is complete.'],
          ['Invoice preferences', 'Generate draft invoices when a job is completed.'],
        ].map(([title, description]) => (
          <Grid key={title} size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2.5, height: '100%', border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.7 }}>{title}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{description}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </RenterSectionShell>
  );
}

function OwnerDashboard({ session }) {
  const [activeSection, setActiveSection] = React.useState(() => getStoredOwnerActiveSection());
  const activeNavItem = RENTER_NAV_ITEMS.find((item) => item.value === activeSection);
  const ActiveIcon = activeNavItem?.icon;

  const handleActiveSectionChange = (section) => {
    storeOwnerActiveSection(section);
    setActiveSection(section);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <RenterProfile session={session} />;
      case 'documents':
        return <RenterDocuments />;
      case 'vehicles':
        return <RenterVehicles session={session} />;
      case 'requests':
        return <RenterRequests />;
      case 'rates':
        return <RenterRatesAvailability />;
      case 'jobs':
        return <RenterJobs />;
      case 'invoices':
        return <RenterInvoices />;
      case 'banking':
        return <OwnerBanking session={session} />;
      case 'settings':
        return <RenterSettings />;
      default:
        return <RenterOverview session={session} onNavigate={handleActiveSectionChange} />;
    }
  };

  return (
    <Grid container spacing={2.5} alignItems="flex-start">
      <Grid size={{ xs: 12, md: 3.5, lg: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            border: '1px solid #dbe7ef',
            borderRadius: 1.5,
            position: { md: 'sticky' },
            top: 16,
          }}
        >
          <Stack spacing={0.75}>
            {RENTER_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.value === activeSection;

              return (
                <Button
                  key={item.value}
                  fullWidth
                  startIcon={<Icon />}
                  onClick={() => handleActiveSectionChange(item.value)}
                  sx={{
                    justifyContent: 'flex-start',
                    minHeight: 48,
                    px: 1.6,
                    py: 1.1,
                    borderRadius: 1.25,
                    color: isActive ? '#0f172a' : '#475569',
                    bgcolor: isActive ? '#fef3c7' : 'transparent',
                    textTransform: 'none',
                    fontSize: 14.5,
                    textAlign: 'left',
                    fontWeight: isActive ? 900 : 750,
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    '& .MuiButton-startIcon': {
                      mr: 1.25,
                      minWidth: 24,
                    },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: 22,
                    },
                    '&:hover': { bgcolor: isActive ? '#fde68a' : '#f8fafc' },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, md: 8.5, lg: 9 }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid #dbe7ef', borderRadius: 1.5 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {ActiveIcon && <ActiveIcon sx={{ color: '#f59e0b' }} />}
              <Typography variant="h4" sx={{ fontWeight: 950 }}>
                {activeNavItem?.label || 'Dashboard'}
              </Typography>
            </Stack>
            {renderActiveSection()}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}

function VehicleListingCard({ vehicle, showAction = true }) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid #dbe7ef',
        backgroundColor: '#ffffff',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 20px 48px rgba(15, 23, 42, 0.1)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={vehicle.image}
          alt={vehicle.type}
          sx={{ width: '100%', height: 184, objectFit: 'cover', display: 'block' }}
        />
        <Chip
          size="small"
          icon={<ShieldOutlined />}
          label="Verified"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: 'rgba(255,255,255,0.94)',
            fontWeight: 800,
          }}
        />
      </Box>
      <Stack spacing={1.2} sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontWeight: 900 }}>{vehicle.type}</Typography>
          <Typography sx={{ fontWeight: 900, color: '#0f766e' }}>{vehicle.pricing}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {vehicle.owner}
        </Typography>
        <Divider />
        <Stack spacing={0.8}>
          <Stack direction="row" spacing={1}>
            <SpeedOutlined fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2">{vehicle.capacity}</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <LocationOnOutlined fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2">{vehicle.location}</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <AccessTimeOutlined fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2">{vehicle.availability}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
          <Chip size="small" label={vehicle.availability} variant="outlined" />
          {showAction && (
            <Button variant="contained" size="small">
              Request
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function FeaturedVehiclesCarousel() {
  const carouselItems = [...VEHICLE_LISTINGS, ...VEHICLE_LISTINGS];

  return (
    <Box
      sx={{
        overflow: 'hidden',
        mx: { xs: -2, md: 0 },
        px: { xs: 2, md: 0 },
        '@keyframes marketplaceVehicleScroll': {
          '0%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(-50%)' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: 'max-content',
          animation: 'marketplaceVehicleScroll 42s linear infinite',
          '&:hover': { animationPlayState: 'paused' },
        }}
      >
        {carouselItems.map((vehicle, index) => (
          <Box
            key={`${vehicle.type}-${index}`}
            sx={{
              width: { xs: 280, sm: 330, md: 360 },
              flex: '0 0 auto',
            }}
          >
            <VehicleListingCard vehicle={vehicle} showAction={false} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function BookingRequests() {
  return (
    <Grid container spacing={1.5}>
      {BOOKING_STATUSES.map((status) => (
        <Grid key={status.label} size={{ xs: 6, md: 2.4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 1.5,
              border: '1px solid #dbe7ef',
              borderTop: `4px solid ${status.color}`,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {status.label}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              {status.count}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

function MarketplaceProfileSummaryItem({ label, value }) {
  return (
    <Paper elevation={0} sx={{ p: 1.6, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 850, mt: 0.4, wordBreak: 'break-word' }}>
        {value || 'Not provided'}
      </Typography>
    </Paper>
  );
}

function MarketplaceProfileSummary({ session }) {
  const profileItems = [
    ['First name', session.firstName || session.user?.first_name],
    ['Last name', session.lastName || session.user?.last_name],
    ['Phone number', session.phone || session.user?.phone_number],
    ['Email', session.email || session.user?.email],
    ['Account type', getMarketplaceRoleLabel(session.type)],
    ['Address', session.address || session.user?.address],
    ['City', session.city || session.user?.city],
    ['Country', session.country || session.user?.country],
    ['Postal code', session.postalCode || session.user?.postal_code],
  ];

  return (
    <Grid container spacing={1.5}>
      {profileItems.map(([label, value]) => (
        <Grid key={label} size={{ xs: 12, sm: label === 'Address' ? 12 : 6 }}>
          <MarketplaceProfileSummaryItem label={label} value={value} />
        </Grid>
      ))}
    </Grid>
  );
}

function MarketplaceProfileDialog({ open, onClose, session }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <PersonOutlined sx={{ color: '#0f766e' }} />
          <Typography variant="h6" sx={{ fontWeight: 850 }}>
            Profile
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip icon={<BadgeOutlined />} label={`Phone: ${session.phone}`} color="success" />
            <Chip label={`Account: ${getMarketplaceRoleLabel(session.type)}`} variant="outlined" />
          </Stack>
          <MarketplaceProfileSummary session={session} />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function MarketplaceSettingsDialog({ open, onClose, session, onSessionChange, onLogout }) {
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [preferences, setPreferences] = React.useState(() => getStoredMarketplacePreferences());
  const [error, setError] = React.useState('');
  const [preferenceSaved, setPreferenceSaved] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setPreferences(getStoredMarketplacePreferences());
      setPreferenceSaved('');
    }
  }, [open]);

  const handlePreferenceChange = (field) => (event) => {
    const updatedPreferences = {
      ...preferences,
      [field]: event.target.checked,
    };

    setPreferences(updatedPreferences);
    storeMarketplacePreferences(updatedPreferences);
    setPreferenceSaved('Notification settings updated.');
  };

  const handleLogout = async () => {
    try {
      setError('');
      setIsLoggingOut(true);
      await logoutMarketplaceSession(session);
      onLogout();
    } catch (logoutError) {
      setError(getApiErrorMessage(logoutError, 'Could not log out. Please try again.'));
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <SettingsOutlined sx={{ color: '#0f766e' }} />
          <Typography variant="h6" sx={{ fontWeight: 850 }}>
            Settings
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="warning">{error}</Alert>}

          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
            <Typography sx={{ fontWeight: 850, mb: 1 }}>Marketplace account</Typography>
            <Stack spacing={0.8}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Phone number: {session.phone}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Account type: {getMarketplaceRoleLabel(session.type)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Token type: {session.tokenType || 'bearer'}
              </Typography>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
            <Typography sx={{ fontWeight: 850, mb: 1 }}>Profile details</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Update the personal details shown on your marketplace profile.
            </Typography>
            <MarketplaceProfileForm
              session={session}
              onSaved={onSessionChange}
              submitLabel="Save Profile Changes"
            />
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <NotificationsOutlined sx={{ color: '#f59e0b' }} />
              <Typography sx={{ fontWeight: 850 }}>Reports and notifications</Typography>
            </Stack>
            {preferenceSaved && (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                {preferenceSaved}
              </Alert>
            )}
            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.weeklyReports}
                    onChange={handlePreferenceChange('weeklyReports')}
                  />
                }
                label="Subscribe to weekly marketplace reports"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.bookingNotifications}
                    onChange={handlePreferenceChange('bookingNotifications')}
                  />
                }
                label="Notify me when a client books one of my vehicles"
              />
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #dbe7ef' }}>
            <Typography sx={{ fontWeight: 850, mb: 1 }}>Session</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Logout ends the current marketplace session and removes the stored marketplace token
              from this browser.
            </Typography>
            <Button
              color="error"
              variant="outlined"
              startIcon={<LogoutOutlined />}
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </Paper>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function MarketplaceNotificationsDialog({ open, onClose }) {
  const notifications = [
    ['Lease request waiting', 'Kopano Retail Logistics requested Toyota Hilux 2021.'],
    ['Document reminder', 'ID / Passport upload is still pending.'],
    ['Invoice update', 'INV-2051 is due on 26 May 2026.'],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <NotificationsOutlined sx={{ color: '#f59e0b' }} />
          <Typography variant="h6" sx={{ fontWeight: 850 }}>
            Notifications
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.2} sx={{ pt: 1 }}>
          {notifications.map(([title, message]) => (
            <Paper
              key={title}
              elevation={0}
              sx={{ p: 2, borderRadius: 1.5, border: '1px solid #dbe7ef' }}
            >
              <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {message}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function MarketplaceWorkspace({ session, selectedType, onTypeChange, onSessionChange, onLogout }) {
  const [tab, setTab] = React.useState(
    () =>
      localStorage.getItem(MARKETPLACE_VIEW_KEY) ||
      (selectedType === 'leaser' ? 'leaser' : 'renter')
  );
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = React.useState(false);
  const [logoutError, setLogoutError] = React.useState('');
  const [roleError, setRoleError] = React.useState('');
  const [roleSuccess, setRoleSuccess] = React.useState('');

  React.useEffect(() => {
    if (selectedType === 'leaser' && tab === 'renter') {
      setTab('leaser');
      localStorage.setItem(MARKETPLACE_VIEW_KEY, 'leaser');
    } else if (selectedType === 'renter' && tab === 'leaser') {
      setTab('renter');
      localStorage.setItem(MARKETPLACE_VIEW_KEY, 'renter');
    }
  }, [selectedType, tab]);

  const handleTabChange = (event, value) => {
    setTab(value);
    localStorage.setItem(MARKETPLACE_VIEW_KEY, value);
  };

  const handleRoleChange = async (type) => {
    if (type === selectedType) {
      return;
    }

    try {
      setRoleError('');
      setRoleSuccess('');
      setIsUpdatingRole(true);
      const updatedSession = await updateMarketplaceRole(session, type);
      onTypeChange(updatedSession.type || type);
      onSessionChange(updatedSession);
      setRoleSuccess(`Marketplace role updated to ${getMarketplaceRoleLabel(updatedSession.type || type)}.`);
    } catch (roleUpdateError) {
      setRoleError(getApiErrorMessage(roleUpdateError, 'Could not update marketplace role. Please try again.'));
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleDirectLogout = async () => {
    try {
      setLogoutError('');
      setIsLoggingOut(true);
      await logoutMarketplaceSession(session);
      onLogout();
    } catch (logoutRequestError) {
      setLogoutError(getApiErrorMessage(logoutRequestError, 'Could not log out. Please try again.'));
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 1.5,
            border: '1px solid #dbe7ef',
            backgroundColor: '#ffffff',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                Welcome, {session.firstName || 'Marketplace'} {session.lastName || 'User'}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Marketplace session active for {session.phone}
              </Typography>
              {logoutError && (
                <Typography variant="body2" sx={{ color: 'error.main', mt: 0.7 }}>
                  {logoutError}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip icon={<BadgeOutlined />} label="Phone verified" color="success" />
              <IconButton
                aria-label="Open notifications"
                onClick={() => setNotificationsOpen(true)}
                sx={{ border: '1px solid #dbe7ef' }}
              >
                <NotificationsOutlined />
              </IconButton>
              <IconButton
                aria-label="Open profile"
                onClick={() => setProfileOpen(true)}
                sx={{ border: '1px solid #dbe7ef', p: 0.4 }}
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#0f172a', fontSize: 14, fontWeight: 900 }}>
                  {(session.firstName?.[0] || 'M')}{(session.lastName?.[0] || 'U')}
                </Avatar>
              </IconButton>
              <IconButton
                aria-label="Open settings"
                onClick={() => setSettingsOpen(true)}
                sx={{ border: '1px solid #dbe7ef' }}
              >
                <SettingsOutlined />
              </IconButton>
              <Button
                color="error"
                variant="outlined"
                startIcon={<LogoutOutlined />}
                onClick={handleDirectLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={1.5}>
          <UserTypeSelection
            selectedType={selectedType}
            onSelect={handleRoleChange}
            title="Change marketplace role"
            helperText="Switch between Owner and Renter. This updates your marketplace account role."
            availableTypes={USER_TYPES.filter((type) => type.value !== 'both')}
            disabled={isUpdatingRole}
          />
          {roleError && <Alert severity="warning">{roleError}</Alert>}
          {roleSuccess && <Alert severity="success">{roleSuccess}</Alert>}
        </Stack>

        <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #dbe7ef', overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="renter" label="Owner Dashboard" />
            <Tab value="leaser" label="Renter Dashboard" />
            <Tab value="bookings" label="Booking Requests" />
          </Tabs>
        </Paper>

        {(tab === 'renter' || selectedType === 'both') && tab === 'renter' && (
          <OwnerDashboard session={session} />
        )}
        {(tab === 'leaser' || selectedType === 'both') && tab === 'leaser' && (
          <MarketplaceRenterDashboard />
        )}
        {tab === 'bookings' && <BookingRequests />}
      </Stack>

      <MarketplaceProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        session={session}
      />
      <MarketplaceSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        session={session}
        onSessionChange={onSessionChange}
        onLogout={onLogout}
      />
      <MarketplaceNotificationsDialog
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </Container>
  );
}

export default function HoneyBadgerMarketplacePage() {
  const [restoredMarketplaceSession] = React.useState(() => getStoredMarketplaceSession());
  const [authOpen, setAuthOpen] = React.useState(false);
  const [authNeedsRoleSelection, setAuthNeedsRoleSelection] = React.useState(false);
  const [authFocusRequest, setAuthFocusRequest] = React.useState(0);
  const [selectedType, setSelectedType] = React.useState(
    () =>
      restoredMarketplaceSession?.type ||
      localStorage.getItem(MARKETPLACE_ROLE_KEY) ||
      'renter'
  );
  const [session, setSession] = React.useState(() =>
    restoredMarketplaceSession?.firstName && restoredMarketplaceSession?.lastName
      ? restoredMarketplaceSession
      : null
  );
  const [verifiedSession, setVerifiedSession] = React.useState(() =>
    restoredMarketplaceSession && (!restoredMarketplaceSession.firstName || !restoredMarketplaceSession.lastName)
      ? restoredMarketplaceSession
      : null
  );
  const authSectionRef = React.useRef(null);
  const phoneInputRef = React.useRef(null);
  const showPublicLanding = !session && !verifiedSession;

  React.useEffect(() => {
    if (!restoredMarketplaceSession) {
      return undefined;
    }

    let isMounted = true;

    const hydrateStoredProfile = async () => {
      try {
        const refreshedSession = await refreshMarketplaceSession(restoredMarketplaceSession);
        const hydratedSession = await getMarketplaceProfile(refreshedSession);

        if (!isMounted) {
          return;
        }

        setSelectedType(hydratedSession.type || 'renter');
        if (hydratedSession.firstName && hydratedSession.lastName) {
          setSession(hydratedSession);
          setVerifiedSession(null);
        } else {
          setSession(null);
          setVerifiedSession(hydratedSession);
        }
      } catch (profileError) {
        console.error('Failed to hydrate marketplace profile:', profileError);
        if (profileError?.response?.status === 401 || profileError?.response?.status === 403) {
          clearStoredMarketplaceSession();
          if (isMounted) {
            setSession(null);
            setVerifiedSession(null);
          }
        }
      }
    };

    hydrateStoredProfile();

    return () => {
      isMounted = false;
    };
  }, [restoredMarketplaceSession]);

  React.useEffect(() => {
    if (!authOpen || session || verifiedSession || authFocusRequest === 0) {
      return undefined;
    }

    const scrollTimer = setTimeout(() => {
      authSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    const focusTimer = authNeedsRoleSelection
      ? null
      : setTimeout(() => {
          phoneInputRef.current?.focus();
        }, 450);

    return () => {
      clearTimeout(scrollTimer);
      if (focusTimer) {
        clearTimeout(focusTimer);
      }
    };
  }, [authFocusRequest, authNeedsRoleSelection, authOpen, session, verifiedSession]);

  const handleMarketplaceTypeChange = (type) => {
    setSelectedType(type);
    localStorage.setItem(MARKETPLACE_ROLE_KEY, type);
  };

  const handleOpenMarketplaceAuth = (type) => {
    const nextType = typeof type === 'string' ? type : '';

    if (nextType) {
      handleMarketplaceTypeChange(nextType);
      setAuthNeedsRoleSelection(false);
    } else {
      setAuthNeedsRoleSelection(true);
    }

    setAuthOpen(true);
    setAuthFocusRequest((current) => current + 1);
  };

  const handleMarketplaceRoleIntent = (type) => {
    handleMarketplaceTypeChange(type);
    setAuthNeedsRoleSelection(false);
    setAuthOpen(true);
    setAuthFocusRequest((current) => current + 1);
  };

  const handleMarketplaceSessionChange = (updatedSession) => {
    storeMarketplaceSession(updatedSession);
    setSelectedType(updatedSession.type || 'renter');
    setSession(updatedSession);
    setVerifiedSession(null);
  };

  const handleMarketplaceVerified = (marketplaceSession) => {
    setSelectedType(marketplaceSession.type || selectedType);

    if (marketplaceSession.firstName && marketplaceSession.lastName) {
      handleMarketplaceSessionChange(marketplaceSession);
      return;
    }

    setVerifiedSession(marketplaceSession);
  };

  const handleMarketplaceLogout = () => {
    clearStoredMarketplaceSession();
    setSession(null);
    setVerifiedSession(null);
    setSelectedType('renter');
    setAuthOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>HoneyBadger Marketplace</title>
      </Helmet>

      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: '#f8fafc',
          color: '#0f172a',
        }}
      >
        <MarketplaceHeader />

        <Box component="main" sx={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
          {showPublicLanding && (
            <>
              <HeroSection onOpenAuth={handleOpenMarketplaceAuth} />
              <CategoryAndTrustSection />
            </>
          )}

          {!session && authOpen && !verifiedSession && (
            <Container
              ref={authSectionRef}
              maxWidth={authNeedsRoleSelection ? 'lg' : 'md'}
              sx={{ py: { xs: 4, md: 6 } }}
            >
              {authNeedsRoleSelection ? (
                <MarketplaceRoleChoice
                  selectedType={selectedType}
                  onSelect={handleMarketplaceRoleIntent}
                />
              ) : (
                <OtpLoginRegister
                  preferredMode={selectedType}
                  onVerified={handleMarketplaceVerified}
                  phoneInputRef={phoneInputRef}
                  onChangeRole={() => setAuthNeedsRoleSelection(true)}
                />
              )}
            </Container>
          )}

          {!session && verifiedSession && (
            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
              <ProfileCompletion
                session={verifiedSession}
                onComplete={handleMarketplaceSessionChange}
              />
            </Container>
          )}

          {session && (
            <MarketplaceWorkspace
              session={session}
              selectedType={selectedType}
              onTypeChange={handleMarketplaceTypeChange}
              onSessionChange={handleMarketplaceSessionChange}
              onLogout={handleMarketplaceLogout}
            />
          )}

          {showPublicLanding && (
            <Container maxWidth="xl" sx={{ pb: { xs: 5, md: 7 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ md: 'flex-end' }}
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
                    Featured vehicles
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>
                    Sample capacity and rates across South African provinces.
                  </Typography>
                </Box>
                <Chip
                  icon={<PaymentsOutlined />}
                  label="Rates shown before booking request"
                  sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 800 }}
                />
              </Stack>
              <FeaturedVehiclesCarousel />
            </Container>
          )}
        </Box>
      </Box>
    </>
  );
}
